import { useEffect, useState } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Settings,
  Plus,
  Save,
  CheckCircle2,
  Loader2,
  Copy,
  Zap,
  Archive,
  Eye,
  EyeOff,
  Mail,
  Send,
  Building2,
  Upload,
  Trash2,
  Globe,
  Download,
  Lock,
  Shield,
  Cpu,
  Package,
  Gauge,
  AlertTriangle,
  Activity,
  History,
  RotateCcw,
  PauseCircle,
  ScrollText,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Info,
  XCircle,
  CheckCircle,
  Clock,
  Server,
  User,
  ExternalLink,
  Hash,
  Timer,
  MemoryStick,
  Wifi,
  WifiOff,
  Layers,
  Link2,
} from "lucide-react";
import {
  RUNTIME_CONFIG_PRESETS,
  RUNTIME_CONFIG_PRESET_LABELS,
  RUNTIME_CONFIG_PRESET_DESCRIPTIONS,
  RUNTIME_CONFIG_TIER_LABELS,
  RUNTIME_CONFIG_TIER_DESCRIPTIONS,
  RUNTIME_CONFIG_LOCKED_BOUNDS,
  getPresetSettings,
  projectedLoadPerMinute,
  type RuntimeConfigSettings,
  type RuntimeConfigPreset,
  type RuntimeConfigTier,
} from "@shared/v2/runtime-config";
import { applyRuntimeConfig } from "@/lib/queryClient";

type SettingsTab = "email-settings" | "branding" | "governance" | "backup" | "api-keys" | "live-refresh" | "system-clock" | "system-log";

const TABS: { key: SettingsTab; label: string; icon: typeof Settings; permission?: string }[] = [
  { key: "email-settings", label: "Email Settings", icon: Mail },
  { key: "branding", label: "Branding", icon: Building2 },
  { key: "governance", label: "Governance Defaults", icon: Shield },
  { key: "backup", label: "Database Backup", icon: Archive },
  { key: "api-keys", label: "Agent API Keys", icon: Cpu },
  { key: "live-refresh", label: "Live Refresh & Performance", icon: Gauge, permission: "manage_runtime_config" },
  { key: "system-clock", label: "System Clock", icon: Clock },
  { key: "system-log", label: "System Log", icon: ScrollText, permission: "view_system_logs" },
];

export default function V2SettingsPage() {
  const { containerClass } = useLayout();
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>("email-settings");

  const visibleTabs = TABS.filter((t) => !t.permission || hasPermission(t.permission));

  const unreadQuery = useQuery<{ count: number }>({
    queryKey: ["/api/system-logs/unread-count"],
    enabled: hasPermission("view_system_logs"),
    refetchInterval: 60_000,
  });
  const unreadCount = unreadQuery.data?.count || 0;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #0f0c29, #1a1a3e, #24243e)" }}>
      <div className={`${containerClass} mx-auto p-6`}>
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-7 h-7 text-cyan-400" />
          <h1
            className="text-2xl font-bold text-white tracking-wide"
            data-testid="text-settings-title"
          >
            System Settings
          </h1>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <Button
                key={tab.key}
                variant={isActive ? "default" : "ghost"}
                className={`${isActive ? "" : "text-white/60"} relative`}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {tab.label}
                {tab.key === "system-log" && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1" data-testid="badge-unread-errors">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {activeTab === "email-settings" && <EmailSettingsTab />}
        {activeTab === "branding" && <BrandingTab />}
        {activeTab === "governance" && <GovernanceDefaultsTab />}
        {activeTab === "backup" && <DatabaseBackupTab />}
        {activeTab === "api-keys" && <AgentApiKeysTab />}
        {activeTab === "live-refresh" && <LiveRefreshTab />}
        {activeTab === "system-clock" && <SystemClockTab />}
        {activeTab === "system-log" && <SystemLogTab onAcknowledge={() => unreadQuery.refetch()} />}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Live Refresh & Performance tab
// ──────────────────────────────────────────────────────────────────────────
// Self-tuning panel for IT admins. Reads current settings from the server,
// lets the admin pick a preset (or hand-tune intervals/behaviour), shows
// projected load math, exposes diagnostics + history + emergency controls,
// and writes back via PATCH /api/admin/runtime-config. After every successful
// save we invalidate /api/runtime-config and call applyRuntimeConfig() so
// the saving admin's own browser switches gears immediately.

const TIER_INTERVAL_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "Off (manual refresh only)" },
  { value: 10, label: "10 seconds (minimum)" },
  { value: 15, label: "15 seconds" },
  { value: 30, label: "30 seconds" },
  { value: 60, label: "1 minute" },
  { value: 120, label: "2 minutes" },
  { value: 300, label: "5 minutes" },
  { value: 600, label: "10 minutes" },
  { value: 900, label: "15 minutes" },
  { value: 1800, label: "30 minutes" },
];

interface RuntimeConfigResponse {
  data: {
    settings: RuntimeConfigSettings;
    updatedAt: string;
    updatedBy: string | null;
    reason: string | null;
    lockedBounds: typeof RUNTIME_CONFIG_LOCKED_BOUNDS;
  };
}

interface RuntimeConfigHistoryEntry {
  id: string;
  changedBy: string;
  changeType: string;
  reason: string | null;
  settings: RuntimeConfigSettings;
  changedAt: string;
}

interface HealthCheckResponse {
  data: {
    samples: Array<{ name: string; durationMs: number; ok: boolean; error?: string }>;
    totalMs: number;
    slowestMs: number;
    slowestName: string;
    ranAt: string;
  };
}

function LiveRefreshTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const cfgQuery = useQuery<RuntimeConfigResponse>({
    queryKey: ["/api/runtime-config"],
  });
  const historyQuery = useQuery<{ data: RuntimeConfigHistoryEntry[] }>({
    queryKey: ["/api/admin/runtime-config/history"],
  });

  const [draft, setDraft] = useState<RuntimeConfigSettings | null>(null);
  const [reason, setReason] = useState("");
  const [userCount, setUserCount] = useState(20);
  const [healthCheck, setHealthCheck] = useState<HealthCheckResponse["data"] | null>(null);

  // Sync draft from server data once on first load (and after rollback /
  // reset / pause server-side mutations that re-fetch the query). We
  // deliberately don't reset on every refetch — that would clobber the
  // admin's in-progress edits if the configuration tier auto-refreshed.
  const serverSettings = cfgQuery.data?.data.settings;
  const serverUpdatedAt = cfgQuery.data?.data.updatedAt;
  useEffect(() => {
    if (serverSettings && draft === null) {
      setDraft(serverSettings);
    }
  }, [serverSettings, draft]);
  // When the server timestamp changes (= someone else, or a rollback,
  // changed it), re-baseline the draft.
  useEffect(() => {
    if (serverSettings && serverUpdatedAt) {
      setDraft(serverSettings);
      setReason("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverUpdatedAt]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/runtime-config"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/runtime-config/history"] });
  };

  const onSettingsChanged = (s: RuntimeConfigSettings) => {
    applyRuntimeConfig(s);
    refreshAll();
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: { settings: RuntimeConfigSettings; reason: string }) => {
      const res = await apiRequest("PATCH", "/api/admin/runtime-config", payload);
      return (await res.json()) as RuntimeConfigResponse;
    },
    onSuccess: (resp) => {
      toast({ title: "Settings saved", description: "New cadence is now active." });
      onSettingsChanged(resp.data.settings);
      setReason("");
    },
    onError: (err: Error) => showFromApiError("Save failed", err),
  });

  const rollbackMutation = useMutation({
    mutationFn: async (historyId: string) => {
      const res = await apiRequest("POST", `/api/admin/runtime-config/rollback/${historyId}`, {});
      return (await res.json()) as RuntimeConfigResponse;
    },
    onSuccess: (resp) => {
      toast({ title: "Rolled back", description: "Previous settings restored." });
      onSettingsChanged(resp.data.settings);
    },
    onError: (err: Error) => showFromApiError("Rollback failed", err),
  });

  const pauseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/runtime-config/emergency-pause", {
        reason: "Emergency pause invoked from Settings tab.",
      });
      return (await res.json()) as RuntimeConfigResponse;
    },
    onSuccess: (resp) => {
      toast({ title: "Auto-refresh paused", description: "All tiers are now off." });
      onSettingsChanged(resp.data.settings);
    },
    onError: (err: Error) => showFromApiError("Emergency pause failed", err),
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/runtime-config/reset-defaults", {
        reason: "Reset to recommended defaults from Settings tab.",
      });
      return (await res.json()) as RuntimeConfigResponse;
    },
    onSuccess: (resp) => {
      toast({ title: "Defaults restored", description: "Balanced preset applied." });
      onSettingsChanged(resp.data.settings);
    },
    onError: (err: Error) => showFromApiError("Reset failed", err),
  });

  const healthMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/runtime-config/health-check", {});
      return (await res.json()) as HealthCheckResponse;
    },
    onSuccess: (resp) => setHealthCheck(resp.data),
    onError: (err: Error) => showFromApiError("Health check failed", err),
  });

  if (cfgQuery.isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-16 text-white/60" data-testid="loading-runtime-config">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading runtime configuration…
      </div>
    );
  }

  const dirty = serverSettings ? JSON.stringify(serverSettings) !== JSON.stringify(draft) : false;
  const projected = projectedLoadPerMinute(draft, userCount);

  const setPreset = (preset: RuntimeConfigPreset) => {
    if (preset === "custom") {
      setDraft({ ...draft, preset: "custom" });
    } else {
      setDraft({ ...getPresetSettings(preset) });
    }
  };

  const setInterval = (tier: RuntimeConfigTier, value: number) => {
    setDraft({
      ...draft,
      preset: "custom",
      refreshIntervals: { ...draft.refreshIntervals, [tier]: value },
    });
  };

  return (
    <div className="space-y-6" data-testid="tab-content-live-refresh">
      {/* 1. Status panel */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" /> Current Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-white/80 space-y-1">
          <div data-testid="text-current-preset">
            Preset: <span className="text-cyan-300 font-semibold">{RUNTIME_CONFIG_PRESET_LABELS[draft.preset]}</span>
          </div>
          {cfgQuery.data?.data.updatedAt && (
            <div className="text-xs text-white/50">
              Last changed {new Date(cfgQuery.data.data.updatedAt).toLocaleString()} by{" "}
              <span className="text-white/70">{cfgQuery.data.data.updatedBy || "system"}</span>
              {cfgQuery.data.data.reason && <> — {cfgQuery.data.data.reason}</>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Preset radio */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm">Choose a preset</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-white/60 bg-cyan-500/5 border border-cyan-500/15 rounded-md px-3 py-2 flex items-start gap-2" data-testid="text-network-guidance">
            <Globe className="w-3.5 h-3.5 mt-0.5 text-cyan-300 shrink-0" />
            <span>
              <span className="text-white/80 font-medium">Network note:</span> users on company WiFi or VPN tend to do better with the Conservative preset — over a VPN, round-trips can be 500–800ms and a 30-second poll piles up. Reach for Balanced or Real-time only when most staff are on a stable wired LAN inside the office.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {RUNTIME_CONFIG_PRESETS.map((p) => {
            const active = draft.preset === p;
            return (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`text-left px-3 py-2 rounded-md border transition-colors ${
                  active ? "border-cyan-400 bg-cyan-500/10" : "border-white/10 hover:border-white/30"
                }`}
                data-testid={`preset-${p}`}
              >
                <div className="text-sm text-white font-medium">{RUNTIME_CONFIG_PRESET_LABELS[p]}</div>
                <div className="text-xs text-white/50">{RUNTIME_CONFIG_PRESET_DESCRIPTIONS[p]}</div>
              </button>
            );
          })}
          </div>
        </CardContent>
      </Card>

      {/* 3. Advanced — tier dropdowns + behaviour toggles + locked bounds */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm">Advanced — per-tier intervals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(Object.keys(draft.refreshIntervals) as RuntimeConfigTier[]).map((tier) => (
            <div key={tier} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
              <div className="text-sm">
                <div className="text-white">{RUNTIME_CONFIG_TIER_LABELS[tier]}</div>
                <div className="text-xs text-white/50">{RUNTIME_CONFIG_TIER_DESCRIPTIONS[tier]}</div>
              </div>
              <div className="md:col-span-2">
                <Select
                  value={String(draft.refreshIntervals[tier])}
                  onValueChange={(v) => setInterval(tier, parseInt(v, 10))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid={`select-tier-${tier}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_INTERVAL_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-white/10">
            <label className="flex items-center justify-between text-sm text-white/80">
              <span>Refresh on tab focus</span>
              <Switch
                checked={draft.refetchOnFocus}
                onCheckedChange={(v) => setDraft({ ...draft, preset: "custom", refetchOnFocus: v })}
                data-testid="switch-refetch-focus"
              />
            </label>
            <label className="flex items-center justify-between text-sm text-white/80">
              <span>Refresh in background tabs</span>
              <Switch
                checked={draft.refetchInBackground}
                onCheckedChange={(v) => setDraft({ ...draft, preset: "custom", refetchInBackground: v })}
                data-testid="switch-refetch-background"
              />
            </label>
            <label className="flex items-center justify-between text-sm text-white/80">
              <span>Refresh on reconnect</span>
              <Switch
                checked={draft.refetchOnReconnect}
                onCheckedChange={(v) => setDraft({ ...draft, preset: "custom", refetchOnReconnect: v })}
                data-testid="switch-refetch-reconnect"
              />
            </label>
          </div>

          <div className="text-sm text-white/40 flex items-center gap-2 pt-2 border-t border-white/10">
            <Lock className="w-3 h-3" /> Hard-coded safety bounds — minimum {RUNTIME_CONFIG_LOCKED_BOUNDS.minRefreshSeconds}s,
            history kept for {RUNTIME_CONFIG_LOCKED_BOUNDS.maxHistoryEntries} changes. Cannot be raised from this UI.
          </div>

          {/* Phase 4 of Handoff Deadlines: KPI grading mode. Lives in this
              card because it shares the runtime-config storage + PATCH
              flow; a dedicated tab would be over-kill for a single
              boolean. Off (board date) by default — toggling on grades
              the On-Time Rate column against each item's internal
              handoff deadline, falling back to the board date when no
              internal date is set. */}
          <div className="pt-3 mt-3 border-t border-white/10 space-y-2">
            <label className="flex items-center justify-between text-sm text-white/80">
              <span className="flex flex-col">
                <span>Grade KPI on-time rate against internal handoff deadlines</span>
                <span className="text-sm text-white/40">
                  When on, the "On-Time Rate" tile and per-person column
                  on the KPI dashboard count closures as on-time vs the
                  current internal deadline (set during handoff) instead
                  of the board due date. Items with no internal date
                  fall back to the board date. The "On Original Due"
                  column is unaffected.
                </span>
              </span>
              <Switch
                checked={!!draft.kpiGradeAgainstInternalDeadline}
                onCheckedChange={(v) => setDraft({ ...draft, preset: "custom", kpiGradeAgainstInternalDeadline: v })}
                data-testid="switch-kpi-grade-internal"
              />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* 4. Apply method */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm">Apply method</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <label className="flex items-center gap-2 text-white">
            <input
              type="radio"
              checked={draft.applyMethod === "refresh_required"}
              onChange={() => setDraft({ ...draft, applyMethod: "refresh_required" })}
              data-testid="radio-apply-refresh"
            />
            <span>Refresh required — other users pick up new cadence on their next configuration tier refresh.</span>
          </label>
          <label className="flex items-center gap-2 text-white/40">
            <input type="radio" disabled data-testid="radio-apply-broadcast" />
            <span>Live broadcast (SSE) — coming in a future release. Implementation guide for developers: <code className="text-white/60">future-development-roadmap/live-broadcast-sse.md</code>.</span>
          </label>
        </CardContent>
      </Card>

      {/* 6. Diagnostics — health-check + projected load */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm">Diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => healthMutation.mutate()}
                disabled={healthMutation.isPending}
                data-testid="button-health-check"
              >
                {healthMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                <span className="ml-2">Run health check</span>
              </Button>
              {healthCheck && (
                <span className="text-xs text-white/60" data-testid="text-health-result">
                  Slowest: {healthCheck.slowestName} — {healthCheck.slowestMs}ms · Total {healthCheck.totalMs}ms
                </span>
              )}
            </div>
            {healthCheck && (
              <div className="mt-2 text-xs text-white/50 space-y-0.5">
                {healthCheck.samples.map((s) => (
                  <div key={s.name} className={s.ok ? "" : "text-red-400"}>
                    {s.ok ? "✓" : "✗"} {s.name}: {s.durationMs}ms{s.error ? ` — ${s.error}` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-3">
            <div className="text-xs text-white/60 mb-2">Projected load (rough estimate, assumes all users active)</div>
            <div className="flex items-center gap-3 mb-2">
              <Label className="text-white/70 text-xs">Active users</Label>
              <Input
                type="number"
                className="bg-white/5 border-white/10 text-white w-24"
                value={userCount}
                min={1}
                max={500}
                onChange={(e) => setUserCount(Math.max(1, parseInt(e.target.value || "1", 10)))}
                data-testid="input-user-count"
              />
              <span className="text-sm text-white/80" data-testid="text-projected-load">
                ≈ {projected.toFixed(1)} requests/min from auto-refresh
              </span>
            </div>
            {projected > 600 && (
              <div className="text-xs text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> High projected load — consider Conservative preset.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 7. History */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <History className="w-4 h-4 text-cyan-400" /> Change history
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyQuery.isLoading ? (
            <div className="text-white/50 text-sm">Loading…</div>
          ) : !historyQuery.data?.data.length ? (
            <div className="text-white/50 text-sm">No history yet.</div>
          ) : (
            <div className="text-xs text-white/70 space-y-1 max-h-72 overflow-auto">
              {historyQuery.data.data.map((h) => (
                <div key={h.id} className="flex items-start justify-between gap-2 border-b border-white/5 pb-1" data-testid={`history-row-${h.id}`}>
                  <div>
                    <div className="text-white/90">
                      {new Date(h.changedAt).toLocaleString()} · <span className="text-cyan-300">{h.changeType}</span> · {h.changedBy}
                    </div>
                    <div className="text-white/40">
                      {RUNTIME_CONFIG_PRESET_LABELS[h.settings.preset]} —{" "}
                      live {h.settings.refreshIntervals.liveWork}s ·{" "}
                      ref {h.settings.refreshIntervals.reference}s ·{" "}
                      cfg {h.settings.refreshIntervals.configuration}s
                      {h.reason && <> · "{h.reason}"</>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-cyan-300 hover:text-cyan-200"
                    onClick={() => rollbackMutation.mutate(h.id)}
                    disabled={rollbackMutation.isPending}
                    data-testid={`button-rollback-${h.id}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 8. Emergency panel */}
      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-amber-300 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Emergency controls
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => pauseMutation.mutate()}
            disabled={pauseMutation.isPending}
            data-testid="button-emergency-pause"
          >
            <PauseCircle className="w-4 h-4 mr-1" /> Pause auto-refresh (all tiers off)
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white/70"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending}
            data-testid="button-reset-defaults"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Reset to recommended defaults
          </Button>
        </CardContent>
      </Card>

      {/* 9. Save bar */}
      <div className="sticky bottom-4 z-10">
        <Card className="bg-[#0d1b3e]/95 border-white/20 shadow-xl">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <Input
              placeholder="Reason for change (optional but recommended for audit trail)"
              className="flex-1 min-w-[200px] bg-white/5 border-white/10 text-white"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="input-save-reason"
            />
            <Button
              variant="ghost"
              className="text-white/60"
              onClick={() => serverSettings && setDraft(serverSettings)}
              disabled={!dirty}
              data-testid="button-discard-changes"
            >
              Discard
            </Button>
            <Button
              onClick={() => saveMutation.mutate({ settings: draft, reason })}
              disabled={!dirty || saveMutation.isPending}
              data-testid="button-save-runtime-config"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmailSettingsTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [host, setHost] = useState("");
  const [port, setPort] = useState(587);
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [fromName, setFromName] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [notificationUrl, setNotificationUrl] = useState("");
  // IT alert recipients & policy (saved alongside SMTP — same admin permission, same lifecycle)
  const [alertRecipients, setAlertRecipients] = useState("");
  const [alertSeverityThreshold, setAlertSeverityThreshold] = useState<"critical" | "error">("critical");
  const [alertThrottleMinutes, setAlertThrottleMinutes] = useState(15);
  const [alertDailyDigestEnabled, setAlertDailyDigestEnabled] = useState(false);
  const [alertDailyDigestHour, setAlertDailyDigestHour] = useState(8);
  const [loaded, setLoaded] = useState(false);

  const smtpQuery = useQuery<{ data: { host: string; port: number; secure: boolean; username: string; password: string; fromAddress: string; fromName: string; enabled: boolean; notificationUrl?: string; alertRecipients?: string; alertSeverityThreshold?: "critical" | "error"; alertThrottleMinutes?: number; alertDailyDigestEnabled?: boolean; alertDailyDigestHour?: number; envOverrides?: { username: boolean; password: boolean } } }>({
    queryKey: ["/api/admin/smtp-config"],
  });

  const smtpData = smtpQuery.data?.data;
  const envOverrides = smtpData?.envOverrides || { username: false, password: false };
  if (smtpData && !loaded) {
    setHost(smtpData.host || "");
    setPort(smtpData.port || 587);
    setSecure(smtpData.secure ?? false);
    setUsername(smtpData.username || "");
    setPassword(smtpData.password || "");
    setFromAddress(smtpData.fromAddress || "");
    setFromName(smtpData.fromName || "");
    setEnabled(smtpData.enabled ?? false);
    setNotificationUrl(smtpData.notificationUrl || "");
    setAlertRecipients(smtpData.alertRecipients || "");
    setAlertSeverityThreshold(smtpData.alertSeverityThreshold || "critical");
    setAlertThrottleMinutes(smtpData.alertThrottleMinutes ?? 15);
    setAlertDailyDigestEnabled(!!smtpData.alertDailyDigestEnabled);
    setAlertDailyDigestHour(smtpData.alertDailyDigestHour ?? 8);
    setLoaded(true);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("PUT", "/api/admin/smtp-config", {
        host, port, secure, username, password, fromAddress, fromName, enabled, notificationUrl,
        alertRecipients, alertSeverityThreshold, alertThrottleMinutes, alertDailyDigestEnabled, alertDailyDigestHour,
        confirmPassword,
      }),
    onSuccess: () => {
      setConfirmPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/smtp-config"] });
      toast({ title: "SMTP configuration saved" });
    },
    onError: (err: any) => showFromApiError("Failed to save", err),
  });

  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/smtp-config/test-connection", {
        host, port, secure, username, password, confirmPassword,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Connection failed");
      return data;
    },
    onSuccess: () => toast({ title: "Connection successful", description: "SMTP server responded correctly." }),
    onError: (err: any) => showFromApiError("Connection failed", err),
  });

  const sendTestMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/smtp-config/test-email", { toAddress: testEmail, confirmPassword });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Send failed");
      return data;
    },
    onSuccess: () => toast({ title: "Test email sent", description: `A test email was sent to ${testEmail}.` }),
    onError: (err: any) => showFromApiError("Failed to send", err),
  });

  const sendTestAlertMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/smtp-config/test-alert", { confirmPassword });
      return res.json();
    },
    onSuccess: (data: any) => toast({
      title: "Test alert sent",
      description: `Sent to ${(data?.recipients || []).join(", ") || "configured recipients"}.`,
    }),
    onError: (err: any) => showFromApiError("Failed to send test alert", err),
  });

  if (smtpQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white" data-testid="text-email-settings-title">Email Settings</h2>
        <p className="text-sm text-white/50">Configure SMTP server for outbound email notifications.</p>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-white text-base">SMTP Configuration</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Enabled</Label>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="switch-smtp-enabled"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80">Host</Label>
              <Input
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="smtp.example.com"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-smtp-host"
              />
            </div>
            <div>
              <Label className="text-white/80">Port</Label>
              <Input
                type="number"
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value) || 0)}
                placeholder="587"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-smtp-port"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-white/80 text-sm">Secure (TLS)</Label>
            <Switch
              checked={secure}
              onCheckedChange={setSecure}
              data-testid="switch-smtp-secure"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80">Username {envOverrides.username && <span className="text-[#28C76F] text-xs ml-1">(env var)</span>}</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="user@example.com"
                className="bg-white/5 border-white/10 text-white"
                disabled={envOverrides.username}
                data-testid="input-smtp-username"
              />
              {envOverrides.username && <p className="text-xs text-[#28C76F]/70 mt-1">Sourced from SMTP_USERNAME environment variable.</p>}
            </div>
            <div>
              <Label className="text-white/80">Password {envOverrides.password && <span className="text-[#28C76F] text-xs ml-1">(env var)</span>}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="SMTP password"
                  className="bg-white/5 border-white/10 text-white pr-10"
                  disabled={envOverrides.password}
                  data-testid="input-smtp-password"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="absolute right-0 top-0"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="button-toggle-smtp-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-white/40" /> : <Eye className="w-4 h-4 text-white/40" />}
                </Button>
              </div>
              {envOverrides.password && <p className="text-xs text-[#28C76F]/70 mt-1">Sourced from SMTP_PASSWORD environment variable.</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80">From Address</Label>
              <Input
                value={fromAddress}
                onChange={(e) => setFromAddress(e.target.value)}
                placeholder="noreply@example.com"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-smtp-from-address"
              />
            </div>
            <div>
              <Label className="text-white/80">From Name</Label>
              <Input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Governance System"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-smtp-from-name"
              />
            </div>
          </div>
          <div className="border-t border-white/10 pt-3 mt-2">
            <Label className="text-white/80">Your Admin Password (to confirm it's you)</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Enter YOUR password (the account you're logged in with)"
              data-testid="input-smtp-confirm-password"
            />
            <p className="text-xs text-amber-400/70 mt-1">This is your own password — the account you are currently signed in with.</p>
          </div>
          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !confirmPassword}
              data-testid="button-save-smtp"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Configuration
            </Button>
            <Button
              variant="secondary"
              onClick={() => testConnectionMutation.mutate()}
              disabled={testConnectionMutation.isPending || !host || !confirmPassword}
              data-testid="button-test-connection"
            >
              {testConnectionMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
              Test Connection
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">Send Test Email</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-white/80">Recipient Email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-test-email"
              />
            </div>
            <Button
              onClick={() => sendTestMutation.mutate()}
              disabled={sendTestMutation.isPending || !testEmail.trim() || !confirmPassword}
              data-testid="button-send-test-email"
            >
              {sendTestMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send Test Email
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            Notification URL Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-white/80">Notification URL</Label>
            <Input
              value={notificationUrl}
              onChange={(e) => setNotificationUrl(e.target.value)}
              placeholder="https://tracker.heliotech.ae"
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-notification-url"
            />
            <p className="text-xs text-white/40 mt-1.5">
              Used for all clickable links in email notifications and password reset emails. Enter the full URL with https://. The system automatically appends the action item path.
            </p>
          </div>
          {notificationUrl && (
            <div className="bg-white/5 rounded-md p-3 border border-white/10">
              <p className="text-xs text-white/50 mb-1">Example notification link:</p>
              <p className="text-xs text-cyan-400 font-mono break-all" data-testid="text-notification-url-preview">
                {notificationUrl.replace(/\/+$/, "")}/action-items/8bf309a0-6e0d-...
              </p>
              <p className="text-xs text-white/50 mt-2 mb-1">Example password reset link:</p>
              <p className="text-xs text-cyan-400 font-mono break-all">
                {notificationUrl.replace(/\/+$/, "")}/reset-password?token=abc123...
              </p>
            </div>
          )}
          <p className="text-xs text-white/30">
            This URL takes priority over the Base URL in Branding settings. Changes are saved together with the SMTP configuration above.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10" data-testid="card-it-alerts">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            IT Alert Recipients
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Email addresses that receive alerts when system errors are written to the System Diagnostics Log. Identical events are grouped within the throttle window. A daily digest of unacknowledged errors can also be sent.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-white/80">Recipients</Label>
            <textarea
              value={alertRecipients}
              onChange={(e) => setAlertRecipients(e.target.value)}
              placeholder="it-support@example.com, ops-oncall@example.com"
              rows={3}
              className="mt-1 w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm font-mono placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-cyan-400"
              data-testid="input-alert-recipients"
            />
            <p className="text-xs text-white/40 mt-1.5">
              Comma, semicolon, or newline separated. Leave empty to disable alerts entirely. Alerts are also disabled automatically if SMTP is off.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/80">Severity threshold</Label>
              <Select
                value={alertSeverityThreshold}
                onValueChange={(v) => setAlertSeverityThreshold(v as "critical" | "error")}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-alert-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical only (recommended)</SelectItem>
                  <SelectItem value="error">Error and Critical (noisier)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-white/40 mt-1.5">Warnings and info are never alerted on.</p>
            </div>

            <div>
              <Label className="text-white/80">Throttle window (minutes)</Label>
              <Input
                type="number"
                min={1}
                max={120}
                value={alertThrottleMinutes}
                onChange={(e) => setAlertThrottleMinutes(Math.max(1, Math.min(120, parseInt(e.target.value) || 15)))}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-alert-throttle"
              />
              <p className="text-xs text-white/40 mt-1.5">First occurrence sends immediately; repeats within the window are grouped into a follow-up.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-md px-3 py-2.5">
              <div>
                <Label className="text-white/80 text-sm">Daily digest</Label>
                <p className="text-xs text-white/40">Sends a summary of unacknowledged errors from the last 24h.</p>
              </div>
              <Switch
                checked={alertDailyDigestEnabled}
                onCheckedChange={setAlertDailyDigestEnabled}
                data-testid="switch-alert-digest"
              />
            </div>
            <div>
              <Label className="text-white/80">Digest hour (0–23, server local time)</Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={alertDailyDigestHour}
                onChange={(e) => setAlertDailyDigestHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 8)))}
                className="bg-white/5 border-white/10 text-white"
                disabled={!alertDailyDigestEnabled}
                data-testid="input-alert-digest-hour"
              />
            </div>
          </div>

          <div className="bg-amber-400/5 border border-amber-400/20 rounded-md px-3 py-2.5 text-xs text-amber-200/80">
            <strong>Built-in safeguards:</strong> alerts about email delivery itself are suppressed (loop-protection), and a hard cap of 20 emails per hour applies across all alert types.
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button
              onClick={() => sendTestAlertMutation.mutate()}
              disabled={sendTestAlertMutation.isPending || !confirmPassword || !alertRecipients.trim() || !enabled}
              variant="outline"
              data-testid="button-send-test-alert"
            >
              {sendTestAlertMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send Test Alert
            </Button>
            <p className="text-xs text-white/40">
              Requires the admin password entered above. Bypasses throttle/cap. Saves first if you have unsaved changes.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BrandingTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [companyName, setCompanyName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [fileUploadsEnabled, setFileUploadsEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const brandingQuery = useQuery<{ data: { companyName: string; departmentName: string; logoBase64: string | null; baseUrl: string; fileUploadsEnabled?: boolean } }>({
    queryKey: ["/api/admin/branding"],
  });

  const brandingData = brandingQuery.data?.data;
  if (brandingData && !loaded) {
    setCompanyName(brandingData.companyName || "");
    setDepartmentName(brandingData.departmentName || "");
    setLogoBase64(brandingData.logoBase64 || null);
    setBaseUrl(brandingData.baseUrl || "");
    setFileUploadsEnabled(brandingData.fileUploadsEnabled ?? true);
    setLoaded(true);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("PUT", "/api/admin/branding", {
        companyName, departmentName, logoBase64, baseUrl, fileUploadsEnabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding"] });
      toast({ title: "Branding configuration saved" });
    },
    onError: (err: any) => showFromApiError("Failed to save", err),
  });

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      showFromApiError("File Too Large", { message: "Logo must be under 300 KB." });
      return;
    }
    if (!file.type.startsWith("image/")) {
      showFromApiError("Invalid File", { message: "Please upload an image file (PNG, JPG, SVG)." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoBase64(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  if (brandingQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white" data-testid="text-branding-title">Branding</h2>
        <p className="text-sm text-white/50">Configure company identity used across the system and in email notifications.</p>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">Company Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80">Company Name</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. EDGE Group"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-branding-company-name"
              />
            </div>
            <div>
              <Label className="text-white/80">Department / Division Name</Label>
              <Input
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                placeholder="e.g. Strategy & Development Fund"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-branding-department-name"
              />
              <p className="text-xs text-white/40 mt-1">Optional. Shown below company name.</p>
            </div>
          </div>

          <div>
            <Label className="text-white/80">Company Logo</Label>
            <div className="mt-2 flex items-start gap-4 flex-wrap">
              {logoBase64 ? (
                <div className="relative">
                  <div className="w-32 h-32 rounded-md border border-white/10 bg-white/5 flex items-center justify-center p-2 overflow-hidden">
                    <img
                      src={logoBase64}
                      alt="Company logo"
                      className="max-w-full max-h-full object-contain"
                      data-testid="img-branding-logo-preview"
                    />
                  </div>
                  {fileUploadsEnabled && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute -top-2 -right-2 text-red-400"
                      onClick={() => setLogoBase64(null)}
                      data-testid="button-remove-logo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="w-32 h-32 rounded-md border border-dashed border-white/20 bg-white/5 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white/20" />
                </div>
              )}
              {fileUploadsEnabled && (
                <div className="flex flex-col gap-2">
                  <label
                    className="inline-flex items-center gap-2 cursor-pointer px-4 py-2 rounded-md bg-white/10 text-white text-sm border border-white/10"
                    data-testid="button-upload-logo"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Logo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                      data-testid="input-branding-logo-file"
                    />
                  </label>
                  <p className="text-xs text-white/40">PNG, JPG, or SVG. Max 300 KB.</p>
                </div>
              )}
              {!fileUploadsEnabled && (
                <p className="text-xs text-white/40 self-center">Logo upload is disabled while file uploads are turned off.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">System URL</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-white/80">Base URL</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Globe className="w-4 h-4 text-white/40 shrink-0" />
              <Input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://governance.yourcompany.com"
                className="bg-white/5 border-white/10 text-white flex-1 min-w-[250px]"
                data-testid="input-branding-base-url"
              />
            </div>
            <p className="text-xs text-white/40 mt-1">Used in email notifications to generate clickable links to action items. Include the full URL with https://.</p>
          </div>

          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-branding"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Branding
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-cyan-400" />
            Security & Compliance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white/80">File Uploads</Label>
              <p className="text-xs text-white/40 mt-0.5">
                When disabled, all file upload elements are removed from the application. Use this if your organization's security gateway (e.g. Zscaler) blocks sites with upload capabilities.
              </p>
            </div>
            <Switch
              checked={fileUploadsEnabled}
              onCheckedChange={setFileUploadsEnabled}
              data-testid="switch-file-uploads"
            />
          </div>
          {!fileUploadsEnabled && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-sm text-amber-200">
              File uploads are disabled. The Logo Upload on this page will be hidden from all users.
            </div>
          )}
        </CardContent>
      </Card>

      {(companyName || logoBase64) && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-md p-6 flex items-center gap-4">
              {logoBase64 && (
                <img src={logoBase64} alt="Logo" className="w-12 h-12 object-contain" />
              )}
              <div>
                <div className="text-lg font-bold text-gray-900">{companyName || "Company Name"}</div>
                {departmentName && (
                  <div className="text-sm text-gray-500">{departmentName}</div>
                )}
              </div>
            </div>
            <p className="text-xs text-white/40 mt-2">This is how your branding will appear in email headers.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DatabaseBackupTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [passcodeError, setPasscodeError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingProject, setIsDownloadingProject] = useState(false);
  const [isDownloadingCompiled, setIsDownloadingCompiled] = useState(false);
  const [currentPasscode, setCurrentPasscode] = useState("");
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [isChangingPasscode, setIsChangingPasscode] = useState(false);

  const handleVerifyPasscode = async () => {
    if (!passcode.trim()) {
      setPasscodeError("Please enter the passcode");
      return;
    }
    setIsVerifying(true);
    setPasscodeError("");
    try {
      const csrf = document.cookie.match(/(?:^|;\s*)sdf\.csrf=([^;]*)/);
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrf) headers["x-csrf-token"] = decodeURIComponent(csrf[1]);
      const res = await fetch("/api/admin/backup/verify-passcode", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ passcode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Verification failed" }));
        setPasscodeError(err.message || "Invalid passcode");
        return;
      }
      setIsUnlocked(true);
      setPasscode("");
    } catch {
      setPasscodeError("Failed to verify passcode");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChangePasscode = async () => {
    if (!currentPasscode || !newPasscode || !confirmPasscode) {
      showFromApiError("Missing Information", { message: "All fields are required." });
      return;
    }
    if (newPasscode !== confirmPasscode) {
      showFromApiError("Passcode Mismatch", { message: "New passcodes do not match." });
      return;
    }
    if (newPasscode.length < 6) {
      showFromApiError("Passcode Too Short", { message: "New passcode must be at least 6 characters." });
      return;
    }
    setIsChangingPasscode(true);
    try {
      const csrf = document.cookie.match(/(?:^|;\s*)sdf\.csrf=([^;]*)/);
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrf) headers["x-csrf-token"] = decodeURIComponent(csrf[1]);
      const res = await fetch("/api/admin/backup/change-passcode", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ currentPasscode, newPasscode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Change failed" }));
        throw new Error(err.message || "Failed to change passcode");
      }
      toast({ title: "Passcode changed successfully" });
      setCurrentPasscode("");
      setNewPasscode("");
      setConfirmPasscode("");
    } catch (err: any) {
      showFromApiError("Failed to change passcode", err);
    } finally {
      setIsChangingPasscode(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const res = await fetch("/api/admin/backup", { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Download failed" }));
        throw new Error(err.message || "Download failed");
      }
      const blob = await res.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `governance-backup-${timestamp}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Backup downloaded", description: `File: ${filename}` });
    } catch (err: any) {
      showFromApiError("Backup failed", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadProject = async () => {
    setIsDownloadingProject(true);
    try {
      const res = await fetch("/api/admin/download-project", { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Download failed" }));
        throw new Error(err.message || "Download failed");
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition");
      const match = disposition?.match(/filename="(.+)"/);
      const filename = match?.[1] || `governance-tracker-${new Date().toISOString().split("T")[0]}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Project downloaded", description: `File: ${filename}` });
    } catch (err: any) {
      showFromApiError("Download failed", err);
    } finally {
      setIsDownloadingProject(false);
    }
  };

  const handleDownloadCompiled = async () => {
    setIsDownloadingCompiled(true);
    try {
      const res = await fetch("/api/admin/download-compiled", { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Build failed" }));
        throw new Error(err.message || "Build failed");
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition");
      const match = disposition?.match(/filename="(.+)"/);
      const filename = match?.[1] || `sdf-tracker-compiled-${new Date().toISOString().split("T")[0]}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Compiled package downloaded", description: `File: ${filename}` });
    } catch (err: any) {
      showFromApiError("Download failed", err);
    } finally {
      setIsDownloadingCompiled(false);
    }
  };

  if (!isUnlocked) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="bg-white/5 border-white/10 max-w-sm w-full">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4">
              <Lock className="w-10 h-10 text-[#FF9F43]" />
              <div className="text-center">
                <h3 className="text-white font-semibold text-lg">Restricted Area</h3>
                <p className="text-white/50 text-sm mt-1">Enter the backup passcode to access database backup and source code downloads.</p>
              </div>
              <div className="w-full space-y-3">
                <Input
                  type="password"
                  placeholder="Enter passcode"
                  value={passcode}
                  onChange={(e) => { setPasscode(e.target.value); setPasscodeError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyPasscode()}
                  className="bg-white/5 border-white/10 text-white text-center"
                  data-testid="input-backup-passcode"
                />
                {passcodeError && (
                  <p className="text-red-400 text-xs text-center" data-testid="text-passcode-error">{passcodeError}</p>
                )}
                <Button
                  onClick={handleVerifyPasscode}
                  disabled={isVerifying}
                  className="w-full"
                  data-testid="button-verify-passcode"
                >
                  {isVerifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  {isVerifying ? "Verifying..." : "Unlock"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white" data-testid="text-backup-title">Database Backup</h2>
        <p className="text-sm text-white/50 mt-1">Export a complete snapshot of all application data as a JSON file. This includes action items, events, team members, user accounts (without passwords), governance configuration, SMTP settings, and branding.</p>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-4 py-4">
            <Archive className="w-12 h-12 text-cyan-400/60" />
            <div className="text-center">
              <p className="text-white/80 text-sm">Click the button below to generate and download a full backup of your database.</p>
              <p className="text-white/40 text-xs mt-1">The backup file will contain all data in JSON format with a timestamp in the filename.</p>
            </div>
            <Button
              onClick={handleDownload}
              disabled={isDownloading}
              data-testid="button-download-backup"
            >
              {isDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {isDownloading ? "Generating Backup..." : "Download Full Backup"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10 mt-4">
        <CardContent className="p-4">
          <h3 className="text-white/80 text-sm font-medium mb-2">What's included in the backup:</h3>
          <ul className="text-white/50 text-sm space-y-1">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Action items and all event history</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Team members</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> User accounts (passwords excluded)</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Governance versions, lanes, actors, reference lists</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Workflow rules and field permissions</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> SMTP configuration (password redacted)</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Branding configuration</li>
          </ul>
        </CardContent>
      </Card>

      <WipeAllTasksCard />


      <div className="mt-8 mb-4">
        <h2 className="text-lg font-semibold text-white" data-testid="text-project-download-title">Download Source Code</h2>
        <p className="text-sm text-white/50 mt-1">Download the complete application source code as a ZIP file for offline deployment on another machine.</p>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-4 py-4">
            <Download className="w-12 h-12 text-[#FF9F43]/60" />
            <div className="text-center">
              <p className="text-white/80 text-sm">Download the complete project including source code, dependencies, and build files — ready to deploy.</p>
              <p className="text-white/40 text-xs mt-1">This is a large download (may take a minute to prepare). After downloading, follow the guides in the docs folder.</p>
            </div>
            <Button
              onClick={handleDownloadProject}
              disabled={isDownloadingProject}
              className="bg-[#FF9F43] hover:bg-[#FF9F43]/80 text-black"
              data-testid="button-download-project"
            >
              {isDownloadingProject ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {isDownloadingProject ? "Preparing Download..." : "Download Project (ZIP)"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 mb-4">
        <h2 className="text-lg font-semibold text-white" data-testid="text-compiled-download-title">Download Compiled Package</h2>
        <p className="text-sm text-white/50 mt-1">Download a production-ready compiled package without source code — for distribution to on-premise servers.</p>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-4 py-4">
            <Package className="w-12 h-12 text-[#F24C27]/60" />
            <div className="text-center">
              <p className="text-white/80 text-sm">Download a bundled, minified server binary and pre-built frontend — ready to deploy on any server with Node.js 18+.</p>
              <p className="text-white/40 text-xs mt-1">Source code is not included. This takes about 15–30 seconds to build and package.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 w-full max-w-sm">
              <p className="text-white/60 text-xs font-medium mb-2">Package includes:</p>
              <ul className="text-white/50 text-xs space-y-1">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Bundled & minified server (single file)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Pre-built frontend assets</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Installation guide & config template</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" /> Windows & Linux start scripts</li>
              </ul>
            </div>
            <Button
              onClick={handleDownloadCompiled}
              disabled={isDownloadingCompiled || isDownloadingProject}
              className="bg-[#F24C27] hover:bg-[#F24C27]/80 text-white"
              data-testid="button-download-compiled"
            >
              {isDownloadingCompiled ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              {isDownloadingCompiled ? "Building & Packaging..." : "Download Compiled Package"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 mb-4">
        <h2 className="text-lg font-semibold text-white">Change Backup Passcode</h2>
        <p className="text-sm text-white/50 mt-1">Update the passcode required to access this section.</p>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-6">
          <div className="space-y-3 max-w-sm">
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Current Passcode</Label>
              <Input
                type="password"
                value={currentPasscode}
                onChange={(e) => setCurrentPasscode(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-current-passcode"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">New Passcode</Label>
              <Input
                type="password"
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-passcode"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Confirm New Passcode</Label>
              <Input
                type="password"
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-confirm-passcode"
              />
            </div>
            <Button
              onClick={handleChangePasscode}
              disabled={isChangingPasscode}
              variant="outline"
              className="bg-white/5 border-white/15 text-white hover:bg-white/10"
              data-testid="button-change-passcode"
            >
              {isChangingPasscode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              {isChangingPasscode ? "Changing..." : "Change Passcode"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WipeAllTasksCard — go-live cutover button.
//
// Lives at the bottom of Settings → Database Backup tab (already gated by the
// backup passcode). Performs a one-click wipe of all task-related tables
// while preserving users, roles, governance config, settings, and team
// members. Designed for a single use: clearing pilot/test data the day before
// company-wide launch.
//
// Protections (defence in depth):
//   1. Permission gate — only roles with `wipe_all_tasks` see the card.
//      Seeded to super_admin and dev_admin only.
//   2. Backup acknowledgement checkbox — must tick before proceeding.
//   3. Type-to-confirm — must type "WIPE ALL TASKS" exactly.
//   4. Step-up password — must re-enter login password.
//   5. Server-side: all four are re-verified on the endpoint regardless
//      of what the UI sends. Frontend is convenience, not enforcement.
//
// On success an audit entry is written to the System Diagnostics Log
// (severity=critical → triggers IT alert email via the existing dispatcher
// pipeline) BEFORE the wipe runs, so the audit record survives.
// ─────────────────────────────────────────────────────────────────────────────
function WipeAllTasksCard() {
  const auth = useAuth() as any;
  const canWipe: boolean = !!(auth?.permissions?.includes?.("wipe_all_tasks") || auth?.user?.role === "dev_admin" || auth?.user?.role === "super_admin");

  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const [open, setOpen] = useState(false);
  const [backupAcknowledged, setBackupAcknowledged] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!canWipe) return null;

  const REQUIRED_PHRASE = "WIPE ALL TASKS";
  const phraseMatches = confirmPhrase === REQUIRED_PHRASE;
  const passwordFilled = confirmPassword.length > 0;
  const canSubmit = backupAcknowledged && phraseMatches && passwordFilled && !submitting;

  const resetDialog = () => {
    setBackupAcknowledged(false);
    setConfirmPhrase("");
    setConfirmPassword("");
    setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetDialog();
    setOpen(next);
  };

  const handleWipe = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const csrf = document.cookie.match(/(?:^|;\s*)sdf\.csrf=([^;]*)/);
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrf) headers["x-csrf-token"] = decodeURIComponent(csrf[1]);
      const res = await fetch("/api/admin/wipe-all-tasks", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          confirmPassword,
          confirmPhrase,
          backupAcknowledged,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Wipe failed" }));
        throw new Error(err.message || `Server returned ${res.status}`);
      }
      const result = await res.json();
      const d = result?.deleted || {};
      toast({
        title: "All task data wiped",
        description: `Deleted ${d.action_items ?? 0} action items, ${d.action_item_events ?? 0} events, ${d.sub_assignments ?? 0} sub-tasks, ${d.due_date_revisions ?? 0} due-date revisions, ${d.meeting_minutes ?? 0} meeting minutes, ${d.reminder_log ?? 0} reminder log entries, and reset ${d.ref_sequences ?? 0} reference-number counters. Settings, users, roles, and governance config preserved.`,
      });
      // Invalidate every cache that could still hold task-shaped data.
      queryClient.invalidateQueries();
      handleOpenChange(false);
    } catch (err: any) {
      showFromApiError("Wipe failed", err);
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mt-8 mb-4">
        <h2 className="text-lg font-semibold text-red-300" data-testid="text-wipe-tasks-title">Wipe All Tasks &amp; Task History</h2>
        <p className="text-sm text-white/50 mt-1">
          One-time go-live cutover operation. Permanently deletes every task-related row from the database while preserving everything else.
        </p>
      </div>

      <Card className="bg-red-500/5 border-red-400/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-12 h-12 text-red-400 shrink-0" />
            <div className="flex-1">
              <h3 className="text-red-200 font-semibold text-base">When to use this</h3>
              <p className="text-white/70 text-sm mt-1 leading-relaxed">
                During the pilot / testing phase your team registered demo tasks to validate the system. Before opening the app to the whole organisation, use this button to wipe that test data so day-one KPIs and dashboards start at zero. <strong className="text-red-200">This action cannot be undone</strong> &mdash; take a database backup using the card above first.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-red-500/10 border border-red-400/20 rounded-lg p-3">
                  <h4 className="text-red-200 text-xs font-semibold uppercase tracking-wide mb-2">Will be wiped</h4>
                  <ul className="text-white/70 text-xs space-y-1">
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Action items (including soft-deleted ones in the bin)</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Action item event history (audit trail)</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Sub-task assignments</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Due-date revisions and postpones</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> MD meeting card state (lives on action items)</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Meeting minutes documents</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Reminder send log entries</li>
                    <li className="flex items-start gap-2"><Trash2 className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> Reference-number counters (next task starts at #001)</li>
                  </ul>
                </div>
                <div className="bg-green-500/5 border border-green-400/20 rounded-lg p-3">
                  <h4 className="text-green-300 text-xs font-semibold uppercase tracking-wide mb-2">Will be kept</h4>
                  <ul className="text-white/70 text-xs space-y-1">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> User accounts and login credentials</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> Roles and the customised permissions matrix</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> Team members and departments</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> Governance config (lanes, actors, reference lists, workflow rules, transition permissions)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> SMTP, branding, runtime config, reminder schedule, API keys, backup passcode</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> System Diagnostics Log (IT runbook history)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 shrink-0 mt-0.5" /> Role audit events (matrix change trail)</li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => setOpen(true)}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="button-open-wipe-dialog"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Wipe All Tasks &amp; Task History
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-slate-900 border-red-400/40 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Wipe All Tasks &amp; Task History
            </DialogTitle>
            <DialogDescription className="text-white/70 pt-2 leading-relaxed">
              This will permanently delete every action item, every event, every sub-task, every due-date revision, every meeting minute, and every reminder log entry. Users, roles, governance config, and settings are preserved. <strong className="text-red-200">This cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={backupAcknowledged}
                onCheckedChange={(v) => setBackupAcknowledged(!!v)}
                className="mt-0.5"
                data-testid="checkbox-backup-acknowledged"
              />
              <span className="text-white/80 text-sm leading-snug">
                I have downloaded a database backup within the last hour using the <em>Download Full Backup</em> button above.
              </span>
            </label>

            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">
                Type <code className="bg-red-500/20 text-red-200 px-1.5 py-0.5 rounded font-mono">WIPE ALL TASKS</code> to confirm
              </Label>
              <Input
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder="WIPE ALL TASKS"
                autoComplete="off"
                className="bg-white/5 border-white/10 text-white font-mono"
                data-testid="input-wipe-confirm-phrase"
              />
              {confirmPhrase.length > 0 && !phraseMatches && (
                <p className="text-red-400 text-xs">Phrase must match exactly (uppercase, no quotes).</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Re-enter your login password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-wipe-step-up-password"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
              className="bg-white/5 border-white/15 text-white hover:bg-white/10"
              data-testid="button-cancel-wipe"
            >
              Cancel
            </Button>
            <Button
              onClick={handleWipe}
              disabled={!canSubmit}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              data-testid="button-confirm-wipe"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {submitting ? "Wiping…" : "Wipe All Tasks Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ApiKeyRecord {
  id: string;
  label: string;
  keyPrefix: string;
  actAs: string;
  active: boolean;
  createdBy: string;
  lastUsedAt: string | null;
  createdAt: string;
  allowedScopes?: string[];
  readOnly?: boolean;
  legacyWildcard?: boolean;
}

interface ApiScopeRecord {
  scope: string;
  description: string;
  write: boolean;
}

type SlaConfigResponse = {
  data: {
    dispatchSlaWorkingDays: number;
    minDays: number;
    maxDays: number;
    canEdit: boolean;
  };
};

function GovernanceDefaultsTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const auth = useAuth() as any;
  const isSuperAdmin: boolean = !!(auth?.isSuperAdmin || auth?.user?.role === "super_admin" || auth?.user?.role === "dev_admin");

  const [slaDays, setSlaDays] = useState<number>(3);
  const [loaded, setLoaded] = useState(false);

  const slaQuery = useQuery<SlaConfigResponse>({
    queryKey: ["/api/admin/sla-config"],
  });

  const config = slaQuery.data?.data;
  useEffect(() => {
    if (config && !loaded) {
      setSlaDays(config.dispatchSlaWorkingDays);
      setLoaded(true);
    }
  }, [config, loaded]);

  const minDays = config?.minDays ?? 1;
  const maxDays = config?.maxDays ?? 30;
  const canEdit = !!config?.canEdit && isSuperAdmin;

  const saveMutation = useMutation({
    mutationFn: async (payload: { dispatchSlaWorkingDays: number }) => {
      const res = await apiRequest("PUT", "/api/admin/sla-config", payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "SLA updated", description: `Dispatch SLA set to ${slaDays} working day(s).` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sla-config"] });
      queryClient.invalidateQueries({ queryKey: ["/api/awaiting-dispatch-queue"] });
    },
    onError: (err: any) => {
      showFromApiError(err, "Could not save SLA configuration");
    },
  });

  const onSave = () => {
    if (!Number.isFinite(slaDays) || slaDays < minDays || slaDays > maxDays) {
      toast({
        variant: "destructive",
        title: "Invalid value",
        description: `SLA must be between ${minDays} and ${maxDays} working days.`,
      });
      return;
    }
    saveMutation.mutate({ dispatchSlaWorkingDays: Math.floor(slaDays) });
  };

  return (
    <div className="space-y-4" data-testid="tab-content-governance">
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Dispatch SLA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-white/60">
            How many working days do PMO and the Board Secretary have to dispatch an item upward after the SDF MD approves it?
            Items in the amber zone (≤ 1 working day remaining) trigger a warning email; items past the SLA trigger a breach
            email that also CCs MD admins. Per-person on-time percentage appears on the KPI dashboard.
          </p>

          {slaQuery.isLoading ? (
            <div className="flex items-center gap-2 text-white/60">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading SLA configuration...
            </div>
          ) : (
            <>
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="dispatch-sla-input" className="text-white/80">
                  Dispatch SLA (working days)
                </Label>
                <Input
                  id="dispatch-sla-input"
                  type="number"
                  min={minDays}
                  max={maxDays}
                  step={1}
                  value={slaDays}
                  onChange={(e) => setSlaDays(parseInt(e.target.value, 10) || 0)}
                  disabled={!canEdit}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="input-dispatch-sla-days"
                />
                <p className="text-xs text-white/40">
                  Allowed range: {minDays}–{maxDays} working days. Default is 3.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={onSave}
                  disabled={!canEdit || saveMutation.isPending || slaDays === config?.dispatchSlaWorkingDays}
                  data-testid="button-save-sla-config"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save SLA
                </Button>
                {!canEdit && (
                  <Badge variant="outline" className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                    Read-only — super admin required
                  </Badge>
                )}
                {canEdit && config && slaDays !== config.dispatchSlaWorkingDays && (
                  <span className="text-xs text-white/50">Unsaved change from {config.dispatchSlaWorkingDays} → {slaDays}</span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AgentApiKeysTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newActAs, setNewActAs] = useState("");
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [revokeConfirmPassword, setRevokeConfirmPassword] = useState("");
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const keysQuery = useQuery<{ data: ApiKeyRecord[] }>({
    queryKey: ["/api/admin/api-keys"],
  });

  const scopesQuery = useQuery<{ data: ApiScopeRecord[]; readOnlyScopes: string[] }>({
    queryKey: ["/api/admin/api-scopes"],
    queryFn: async () => {
      const res = await fetch("/api/admin/api-scopes", { credentials: "include" });
      if (!res.ok) return { data: [], readOnlyScopes: [] };
      return res.json();
    },
  });

  const availableScopes = scopesQuery.data?.data ?? [];
  const readOnlyScopeNames = scopesQuery.data?.readOnlyScopes ?? [];
  const selectionIsReadOnly =
    newScopes.length > 0 && newScopes.every((s) => readOnlyScopeNames.includes(s));

  const toggleScope = (scope: string) =>
    setNewScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));

  const teamQuery = useQuery<{ data: { id: string; name: string; team_function: string | null }[] }>({
    queryKey: ["/api/team-members"],
    queryFn: async () => {
      const res = await fetch("/api/team-members", { credentials: "include" });
      if (!res.ok) return { data: [] };
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { label: string; actAs: string; scopes: string[]; confirmPassword: string }) => {
      const res = await apiRequest("POST", "/api/admin/api-keys", data);
      return res.json();
    },
    onSuccess: (response) => {
      setRevealedKey(response.data.rawKey);
      setNewLabel("");
      setNewActAs("");
      setNewScopes([]);
      setConfirmPassword("");
      setShowCreate(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-keys"] });
      toast({ title: "API key created — copy it now, it won't be shown again" });
    },
    onError: (err: any) => {
      showFromApiError("Error", err);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (data: { id: string; confirmPassword: string }) => {
      await apiRequest("DELETE", `/api/admin/api-keys/${data.id}`, { confirmPassword: data.confirmPassword });
    },
    onSuccess: () => {
      setRevokeConfirmPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-keys"] });
      toast({ title: "API key revoked" });
    },
  });

  const keys = keysQuery.data?.data ?? [];
  const activeKeys = keys.filter(k => k.active);
  const revokedKeys = keys.filter(k => !k.active);
  const teamMembers = teamQuery.data?.data ?? [];

  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-white font-semibold text-lg flex items-center gap-2">
                <Cpu className="h-5 w-5 text-[#00CFE8]" />
                Agent API Keys
              </h2>
              <p className="text-white/50 text-sm mt-1">
                Generate API keys for external AI agents to interact with the system
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowCreate(true)}
              className="bg-[#00CFE8] hover:bg-[#00CFE8]/80 text-white"
              data-testid="button-create-api-key"
            >
              <Plus className="h-4 w-4 mr-1" />
              Generate Key
            </Button>
          </div>

          {revealedKey && (
            <div className="mb-4 p-4 rounded-md bg-[#28C76F]/10 border border-[#28C76F]/30" data-testid="card-revealed-key">
              <p className="text-[#28C76F] font-semibold text-sm mb-2">New API Key Created — Copy it now!</p>
              <p className="text-white/50 text-xs mb-2">This key will not be shown again. Store it securely.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-black/30 text-white/90 px-3 py-2 rounded text-xs font-mono break-all" data-testid="text-revealed-key">
                  {revealedKey}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(revealedKey);
                    toast({ title: "API key copied to clipboard" });
                  }}
                  data-testid="button-copy-key"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  Copy
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="mt-2 text-white/40" onClick={() => setRevealedKey(null)}>
                Dismiss
              </Button>
            </div>
          )}

          {showCreate && (
            <div className="mb-4 p-4 rounded-md bg-white/5 border border-white/10" data-testid="card-create-key-form">
              <h3 className="text-white font-medium text-sm mb-3">Generate New API Key</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-white/60 text-xs block mb-1">Label (describe what this key is for)</label>
                  <Input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="e.g., Jeeves AI Assistant"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-key-label"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs block mb-1">Act As (team member the agent represents)</label>
                  <Select value={newActAs} onValueChange={setNewActAs}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-act-as">
                      <SelectValue placeholder="Select team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map(m => (
                        <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/60 text-xs block mb-1">
                    Scopes (what this key is allowed to do)
                  </label>
                  <p className="text-white/40 text-[11px] mb-2">
                    Grant only what the integration needs. A key with no write scopes cannot
                    create, change or delete anything.
                  </p>
                  <div className="space-y-1.5" data-testid="group-key-scopes">
                    {availableScopes.map((s) => (
                      <label
                        key={s.scope}
                        className="flex items-start gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-white/5"
                        data-testid={`checkbox-scope-${s.scope}`}
                      >
                        <input
                          type="checkbox"
                          checked={newScopes.includes(s.scope)}
                          onChange={() => toggleScope(s.scope)}
                          className="mt-0.5 accent-[#00CFE8]"
                        />
                        <span className="text-xs">
                          <span className="font-mono text-white/90">{s.scope}</span>
                          {s.write && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-[#EA5455]/15 text-[#EA5455] text-[10px] font-medium">
                              WRITE
                            </span>
                          )}
                          <span className="block text-white/45">{s.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {newScopes.length > 0 && (
                    <p
                      className={`mt-2 text-[11px] ${selectionIsReadOnly ? "text-[#28C76F]" : "text-[#EA5455]"}`}
                      data-testid="text-scope-summary"
                    >
                      {selectionIsReadOnly
                        ? "Read-only key — this key cannot modify any data."
                        : "This key will be able to CHANGE data."}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-amber-400/70 text-xs block mb-1">Your Admin Password (to confirm it's you)</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Enter YOUR password (the account you're logged in with)"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="input-key-confirm-password"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={!newLabel || !newActAs || newScopes.length === 0 || !confirmPassword || createMutation.isPending}
                    onClick={() => createMutation.mutate({ label: newLabel, actAs: newActAs, scopes: newScopes, confirmPassword })}
                    className="bg-[#28C76F] hover:bg-[#28C76F]/80"
                    data-testid="button-submit-key"
                  >
                    {createMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                    Generate
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {keysQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/50" />
            </div>
          ) : activeKeys.length === 0 ? (
            <div className="text-center py-8 text-white/30">
              <Cpu className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No active API keys. Generate one to enable AI agent access.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeKeys.map(k => (
                <div key={k.id} className="flex items-center justify-between p-3 rounded-md bg-white/4 border border-white/8" data-testid={`api-key-${k.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm">{k.label}</span>
                      <Badge variant="outline" className="text-xs text-[#28C76F] border-[#28C76F]/30">Active</Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                      <span>Key: <code className="text-white/60">{k.keyPrefix}</code></span>
                      <span>Acts as: <span className="text-[#00CFE8]">{k.actAs}</span></span>
                      <span>Created: {new Date(k.createdAt).toLocaleDateString()}</span>
                      {k.lastUsedAt && <span>Last used: {new Date(k.lastUsedAt).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      value={revokeConfirmPassword}
                      onChange={(e) => setRevokeConfirmPassword(e.target.value)}
                      placeholder="Your password"
                      className="bg-white/5 border-white/10 text-white w-36 h-8 text-xs"
                      data-testid={`input-revoke-password-${k.id}`}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                      disabled={!revokeConfirmPassword || revokeMutation.isPending}
                      onClick={() => revokeMutation.mutate({ id: k.id, confirmPassword: revokeConfirmPassword })}
                      data-testid={`button-revoke-${k.id}`}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {revokedKeys.length > 0 && (
            <div className="mt-6">
              <h3 className="text-white/40 text-xs font-medium uppercase tracking-wider mb-2">Revoked Keys</h3>
              <div className="space-y-1">
                {revokedKeys.map(k => (
                  <div key={k.id} className="flex items-center justify-between p-2 rounded-md bg-white/2 border border-white/5 opacity-50">
                    <div className="text-xs text-white/40">
                      <span>{k.label}</span>
                      <span className="ml-3">Key: <code>{k.keyPrefix}</code></span>
                      <span className="ml-3">Was: {k.actAs}</span>
                    </div>
                    <Badge variant="outline" className="text-xs text-red-400/50 border-red-400/20">Revoked</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4">
          <h3 className="text-white/80 text-sm font-medium mb-2">Agent API Endpoints:</h3>
          <p className="text-white/50 text-xs mb-3">Use <code className="text-[#00CFE8]">Authorization: Bearer &lt;api-key&gt;</code> header for all agent endpoints.</p>
          <ul className="space-y-1.5 text-white/50 text-xs">
            <li><code className="text-[#28C76F]">GET</code> <code className="text-white/70">/api/agent/summary</code> — System overview: open items, overdue, breakdowns by lane/status/person</li>
            <li><code className="text-[#28C76F]">GET</code> <code className="text-white/70">/api/agent/tasks</code> — List tasks (filters: status, lane, assignedTo, currentWith, priority, search)</li>
            <li><code className="text-[#28C76F]">GET</code> <code className="text-white/70">/api/agent/tasks/:id</code> — Full task details with sub-tasks, events, latest update</li>
            <li><code className="text-[#28C76F]">GET</code> <code className="text-white/70">/api/agent/people</code> — List all active team members</li>
            <li><code className="text-[#28C76F]">GET</code> <code className="text-white/70">/api/agent/lanes</code> — List authority lanes</li>
            <li><code className="text-[#FF9F43]">POST</code> <code className="text-white/70">/api/agent/tasks</code> — Create a new task</li>
            <li><code className="text-[#FF9F43]">POST</code> <code className="text-white/70">/api/agent/tasks/:id/update</code> — Add a progress update</li>
            <li><code className="text-[#FF9F43]">POST</code> <code className="text-white/70">/api/agent/tasks/:id/handoff</code> — Hand task to another person</li>
            <li><code className="text-[#FF9F43]">POST</code> <code className="text-white/70">/api/agent/tasks/:id/status</code> — Change task status</li>
            <li><code className="text-[#FF9F43]">POST</code> <code className="text-white/70">/api/agent/tasks/:id/sub-tasks</code> — Create sub-task assignments</li>
            <li><code className="text-[#FF9F43]">POST</code> <code className="text-white/70">/api/agent/sub-tasks/:subId/complete</code> — Complete a sub-task</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

const SYSTEM_LOG_CATEGORIES = [
  { key: "email", label: "Email", color: "text-blue-400" },
  { key: "auth", label: "Authentication", color: "text-yellow-400" },
  { key: "database", label: "Database", color: "text-purple-400" },
  { key: "scheduler", label: "Scheduler", color: "text-cyan-400" },
  { key: "api", label: "API", color: "text-green-400" },
  { key: "system", label: "System", color: "text-white/70" },
] as const;

const SEVERITY_CONFIG = {
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/20" },
  warning: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/20" },
  error: { icon: XCircle, color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  critical: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/15 border-red-500/30" },
} as const;

const DETAIL_FIELD_META: Record<string, { label: string; icon?: any; format?: "boolean" | "bytes" | "ms" | "list" | "code" }> = {
  smtpHost: { label: "SMTP Host", icon: Server },
  smtpPort: { label: "SMTP Port", icon: Hash },
  tlsEnabled: { label: "TLS / Secure", icon: Lock, format: "boolean" },
  templateName: { label: "Email Template", icon: Mail },
  recipientCount: { label: "Recipient Count", icon: User },
  errorType: { label: "Error Type", icon: AlertTriangle },
  errorCode: { label: "Error Code", icon: Hash },
  troubleshooting: { label: "Troubleshooting", icon: Info },
  to: { label: "To", icon: Send, format: "list" },
  cc: { label: "CC", icon: Send, format: "list" },
  fromAddress: { label: "From", icon: Mail },
  subject: { label: "Subject", icon: Mail },
  username: { label: "Username", icon: User },
  userId: { label: "User ID", icon: Hash },
  role: { label: "Role", icon: Shield },
  ip: { label: "IP Address", icon: Globe },
  userAgent: { label: "User Agent", icon: Cpu },
  failureReason: { label: "Failure Reason", icon: XCircle },
  consecutiveFailures: { label: "Consecutive Failures", icon: AlertTriangle },
  backoffApplied: { label: "Backoff Applied", icon: Timer, format: "boolean" },
  backoffDelayMs: { label: "Backoff Delay", icon: Timer, format: "ms" },
  reason: { label: "Reason", icon: Info },
  scanned: { label: "Items Scanned", icon: Search },
  warningsFired: { label: "Warnings Fired", icon: AlertTriangle },
  breachesFired: { label: "Breaches Fired", icon: XCircle },
  preDue: { label: "Pre-Due Reminders", icon: Clock },
  overdue: { label: "Overdue Reminders", icon: AlertTriangle },
  nextRunIn: { label: "Next Run In", icon: Timer },
  actionItemId: { label: "Action Item", icon: ExternalLink },
  title: { label: "Title" },
  assignedTo: { label: "Assigned To", icon: User },
  daysHeld: { label: "Days Held", icon: Clock },
  slaDays: { label: "SLA Days", icon: Timer },
  emailSent: { label: "Email Sent", icon: Mail, format: "boolean" },
  recipients: { label: "Recipients", icon: Send, format: "list" },
  bootDurationMs: { label: "Boot Duration", icon: Timer, format: "ms" },
  nodeVersion: { label: "Node.js Version", icon: Server },
  platform: { label: "Platform", icon: Cpu },
  arch: { label: "Architecture", icon: Cpu },
  pid: { label: "Process ID", icon: Hash },
  heapUsedMB: { label: "Heap Used", icon: MemoryStick, format: "bytes" },
  heapTotalMB: { label: "Heap Total", icon: MemoryStick, format: "bytes" },
  rssMB: { label: "RSS Memory", icon: MemoryStick, format: "bytes" },
  purgedLogs: { label: "Purged Logs", icon: Trash2 },
  nodeEnv: { label: "Environment", icon: Server },
  startedAt: { label: "Started At", icon: Clock },
  error: { label: "Error", icon: XCircle },
  stack: { label: "Stack Trace", icon: Cpu, format: "code" },
  // ─── Structured fields written by server/index.ts global error middleware,
  // process-level handlers (unhandledRejection / uncaughtException), the 4xx
  // logger, and the frontend crash reporter (POST /api/system-logs/client-error).
  // Without these explicit entries the UI renders the snake_case key verbatim
  // (e.g. "Pg_code") and the IT tech misses the most actionable info.
  app_version: { label: "App Version", icon: Package },
  request_id: { label: "Request ID", icon: Link2, format: "code" },
  method: { label: "HTTP Method", icon: Globe },
  route: { label: "Route", icon: Globe, format: "code" },
  status: { label: "HTTP Status", icon: Hash },
  duration_ms: { label: "Duration", icon: Timer, format: "ms" },
  query: { label: "Query Params" },
  user_id: { label: "User ID", icon: Hash },
  user_agent: { label: "User Agent", icon: Cpu },
  page_url: { label: "Page URL", icon: Globe, format: "code" },
  kind: { label: "Crash Kind", icon: AlertTriangle },
  error_name: { label: "Error Name", icon: XCircle },
  error_message: { label: "Error Message", icon: XCircle },
  error_type: { label: "Error Type", icon: AlertTriangle },
  error_stack: { label: "Error Stack", icon: Cpu, format: "code" },
  component_stack: { label: "React Component Stack", icon: Cpu, format: "code" },
  breadcrumbs: { label: "Breadcrumbs (last 10)", icon: History, format: "list" },
  first_app_frame: { label: "First App Frame", icon: Cpu, format: "code" },
  troubleshooting_hint: { label: "What To Do", icon: Info },
  // Postgres SQLSTATE error fields (extractPgError in server/v2/systemLog.ts)
  pg_code: { label: "Postgres Code", icon: Hash, format: "code" },
  pg_severity: { label: "Postgres Severity", icon: AlertTriangle },
  pg_table: { label: "Postgres Table", icon: Layers, format: "code" },
  pg_column: { label: "Postgres Column", icon: Layers, format: "code" },
  pg_constraint: { label: "Postgres Constraint", icon: Lock, format: "code" },
  pg_detail: { label: "Postgres Detail", icon: Info },
  pg_hint: { label: "Postgres Hint", icon: Info },
  pg_routine: { label: "Postgres Routine", icon: Cpu, format: "code" },
};

// Long-form text/code that we hide behind a <details> disclosure to keep the
// expanded-row pane scannable. Stack traces and component trees can be 50+
// lines and would push everything else off-screen if rendered inline.
const HIDDEN_DETAIL_KEYS = new Set(["stack", "error_stack", "component_stack"]);

// Fields that carry the most actionable "what to fix / where to look"
// information. Rendered with a yellow highlight so an IT tech's eye lands
// on them first when the row is expanded.
const ELEVATED_HINT_KEYS = new Set(["troubleshooting", "troubleshooting_hint", "pg_hint", "first_app_frame"]);

// Fields rendered in monospace + selectable so they can be copied verbatim
// into a terminal, search box, or git checkout command.
const CODE_STYLED_KEYS = new Set([
  "request_id", "route", "page_url", "first_app_frame",
  "pg_code", "pg_table", "pg_column", "pg_constraint", "pg_routine",
]);

// Convert a snake_case or camelCase key into a human-readable label as a
// fallback for any field not listed in DETAIL_FIELD_META above. Without this,
// snake_case keys like "pg_code" render as "Pg_code" which looks like a typo.
function autoLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (s) => s.toUpperCase());
}

function LogDetailRenderer({ details, relatedEntityType, relatedEntityId }: { details: Record<string, any>; relatedEntityType?: string | null; relatedEntityId?: string | null }) {
  const entries = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== "");
  const mainEntries = entries.filter(([k]) => !HIDDEN_DETAIL_KEYS.has(k));
  // Long-form entries are rendered at the bottom inside collapsible <details>
  // so they don't drown out the structured key/value rows. Order is fixed
  // (stack first, then error_stack, then component_stack) so the same fields
  // always appear in the same place across rows.
  const longFormOrder = ["stack", "error_stack", "component_stack"];
  const longFormEntries = longFormOrder
    .map((k) => entries.find(([key]) => key === k))
    .filter((e): e is [string, any] => !!e);

  // Pull "what to fix" hints to the top so they're the first thing the IT
  // tech sees when the row expands. Order: troubleshooting_hint (the
  // composed plain-English remediation) → pg_hint (Postgres' own hint) →
  // first_app_frame (file:line of the bug). Everything else follows below.
  const elevatedOrder = ["troubleshooting_hint", "troubleshooting", "pg_hint", "first_app_frame"];
  const elevatedEntries = elevatedOrder
    .map((k) => mainEntries.find(([key]) => key === k))
    .filter((e): e is [string, any] => !!e);
  const restEntries = mainEntries.filter(([k]) => !ELEVATED_HINT_KEYS.has(k));

  const copyToClipboard = (text: string) => {
    try { navigator.clipboard?.writeText(text); } catch { /* ignore — older browsers / non-https */ }
  };

  const formatValue = (key: string, value: any): string => {
    const meta = DETAIL_FIELD_META[key];
    if (meta?.format === "boolean") return value ? "Yes" : "No";
    if (meta?.format === "ms") return `${Number(value).toLocaleString()} ms`;
    if (meta?.format === "bytes") return `${value} MB`;
    if (meta?.format === "list" && Array.isArray(value)) return value.join(", ") || "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const renderField = ([key, value]: [string, any]) => {
    const meta = DETAIL_FIELD_META[key];
    const label = meta?.label || autoLabel(key);
    const FieldIcon = meta?.icon;
    const isError = key === "errorType" || key === "error_type" || key === "failureReason" || key === "error" || key === "error_name" || key === "error_message";
    const isElevated = ELEVATED_HINT_KEYS.has(key);
    const isCode = CODE_STYLED_KEYS.has(key) || meta?.format === "code";
    const isList = meta?.format === "list" && Array.isArray(value);
    const isBoolTrue = meta?.format === "boolean" && value === true;
    const isBoolFalse = meta?.format === "boolean" && value === false;
    const showCopy = isCode && typeof value === "string" && value.length > 0;

    return (
      <div
        key={key}
        className={`flex items-start gap-2 text-sm ${isElevated ? "bg-yellow-400/5 border border-yellow-400/15 rounded px-2 py-1.5" : ""}`}
        data-testid={`detail-field-${key}`}
      >
        <div className={`flex items-center gap-1.5 w-[160px] flex-shrink-0 pt-0.5 ${isElevated ? "text-yellow-300/80 font-medium" : "text-white/40"}`}>
          {FieldIcon && <FieldIcon className={`w-3 h-3 ${isError ? "text-red-400" : isElevated ? "text-yellow-400" : "text-white/30"}`} />}
          {label}
        </div>
        <div className={`flex-1 min-w-0 flex items-start gap-1.5`}>
          <span className={`flex-1 break-all ${
            isError ? "text-red-400" :
            isElevated ? "text-yellow-200/95" :
            isBoolTrue ? "text-green-400" :
            isBoolFalse ? "text-white/40" :
            "text-white/70"
          } ${isCode ? "font-mono text-[10.5px] select-all" : ""}`}>
            {key === "tlsEnabled" ? (
              <span className="inline-flex items-center gap-1">
                {value ? <Wifi className="w-3 h-3 text-green-400" /> : <WifiOff className="w-3 h-3 text-white/30" />}
                {value ? "Enabled" : "Disabled"}
              </span>
            ) : key === "actionItemId" && relatedEntityType === "action_item" ? (
              <a href={`/action-items/${value}`} className="text-cyan-400 hover:text-cyan-300 hover:underline">{value}</a>
            ) : key === "page_url" && typeof value === "string" && /^https?:\/\//.test(value) ? (
              <a href={value} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 hover:underline font-mono text-[10.5px]">{value}</a>
            ) : isList ? (
              <ul className="space-y-0.5 list-none">
                {(value as any[]).map((item: any, i: number) => (
                  <li key={i} className="font-mono text-[10.5px] text-white/60 before:content-['•'] before:mr-1.5 before:text-white/30">
                    {typeof item === "string" ? item : JSON.stringify(item)}
                  </li>
                ))}
              </ul>
            ) : key === "status" && typeof value === "number" ? (
              <span className={`font-mono ${value >= 500 ? "text-red-400" : value >= 400 ? "text-yellow-400" : "text-white/70"}`}>{value}</span>
            ) : typeof value === "object" && !Array.isArray(value) ? (
              <pre className="font-mono text-[10.5px] text-white/60 whitespace-pre-wrap break-all m-0">{JSON.stringify(value, null, 2)}</pre>
            ) : (
              formatValue(key, value)
            )}
          </span>
          {showCopy && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); copyToClipboard(String(value)); }}
              title="Copy to clipboard"
              className="text-white/30 hover:text-white/70 flex-shrink-0 pt-0.5"
              data-testid={`button-copy-${key}`}
            >
              <Copy className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {relatedEntityType === "action_item" && relatedEntityId && (
        <a
          href={`/action-items/${relatedEntityId}`}
          className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 hover:underline"
          data-testid={`link-related-item-${relatedEntityId}`}
        >
          <ExternalLink className="w-3 h-3" />
          View Related Action Item
        </a>
      )}
      {elevatedEntries.length > 0 && (
        <div className="space-y-1.5">
          {elevatedEntries.map(renderField)}
        </div>
      )}
      <div className="bg-black/30 rounded p-2.5 space-y-1">
        {restEntries.map(renderField)}
      </div>
      {longFormEntries.map(([key, value]) => {
        const meta = DETAIL_FIELD_META[key];
        const label = meta?.label || autoLabel(key);
        return (
          <details key={key} className="text-sm">
            <summary className="text-white/40 cursor-pointer hover:text-white/60 select-none">{label}</summary>
            <pre className="mt-1 bg-black/40 rounded p-2 text-white/50 whitespace-pre-wrap break-all overflow-x-auto font-mono text-[10.5px]">{value}</pre>
          </details>
        );
      })}
    </div>
  );
}

// Item #9 — Related logs panel inside an expanded row. Lazily fetches
// other entries from the same request_id (or same dedup_key in a
// ±10 min window when the entry has no request_id) so an IT tech can
// see the entire trail of a single failing request without leaving the
// expanded row.
function RelatedLogs({ entryId, onJump }: { entryId: string; onJump: (id: string) => void }) {
  const query = useQuery<{ mode: "request" | "dedup" | "none"; rows: any[] }>({
    queryKey: ["/api/system-logs", entryId, "related"],
    queryFn: async () => {
      const res = await fetch(`/api/system-logs/${entryId}/related`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch related logs");
      return res.json();
    },
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <div className="text-sm text-white/40 flex items-center gap-1.5 pt-1 border-t border-white/5" data-testid={`related-loading-${entryId}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Loading related entries…
      </div>
    );
  }
  const data = query.data;
  if (!data || data.mode === "none" || data.rows.length === 0) return null;

  const label = data.mode === "request"
    ? `Other entries from this same request`
    : `Similar entries within ±10 minutes`;

  return (
    <div className="pt-2 border-t border-white/5 space-y-1" data-testid={`related-logs-${entryId}`}>
      <div className="text-sm text-white/50 flex items-center gap-1.5 font-medium">
        <Link2 className="w-3 h-3 text-cyan-400" />
        {label}
        <span className="text-white/30 font-normal">({data.rows.length})</span>
      </div>
      <div className="space-y-0.5">
        {data.rows.map((r: any) => {
          const sevCfg = SEVERITY_CONFIG[r.severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info;
          const SevIcon = sevCfg.icon;
          const catCfg = SYSTEM_LOG_CATEGORIES.find((c) => c.key === r.category);
          return (
            <button
              key={r.id}
              onClick={(e) => { e.stopPropagation(); onJump(r.id); }}
              className="w-full text-left flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-white/5 transition-colors"
              data-testid={`related-entry-${r.id}`}
            >
              <SevIcon className={`w-3 h-3 flex-shrink-0 ${sevCfg.color}`} />
              <span className={`text-2xs uppercase tracking-wider font-medium w-16 flex-shrink-0 ${catCfg?.color || "text-white/50"}`}>
                {r.category}
              </span>
              <span className="text-white/70 truncate flex-1">{r.message}</span>
              <span className="text-white/30 flex-shrink-0">
                {new Date(r.timestamp).toLocaleTimeString()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// System Clock tab
// ──────────────────────────────────────────────────────────────────────────
// Diagnostic that shows, side by side, the current time as understood by:
//   1. The Node server process     (process.env.TZ or system tz)
//   2. The Postgres session        (SHOW timezone + SELECT NOW())
//   3. The user's browser          (Intl.DateTimeFormat resolved tz)
//   4. The application's configured tz (v2_branding_config.system_timezone)
// A green check appears when all four agree on a single offset; a red flag
// surfaces a mismatch immediately. The IT admin can also pick a new
// application timezone from a curated list (Abu Dhabi GMT+4 default).
// The IANA stored value remains "Asia/Dubai" — the only label the OS,
// browsers, and Postgres recognise — but it's rendered everywhere as
// "Abu Dhabi (GMT+4)" so the company name is what users actually see.

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "Asia/Dubai", label: "Abu Dhabi (GMT+4) — UAE" },
  { value: "UTC", label: "UTC (GMT+0)" },
  { value: "Europe/London", label: "London (GMT+0 / BST GMT+1)" },
  { value: "Asia/Riyadh", label: "Riyadh (GMT+3) — KSA" },
  { value: "Asia/Qatar", label: "Doha (GMT+3) — Qatar" },
  { value: "Asia/Kuwait", label: "Kuwait City (GMT+3)" },
  { value: "Asia/Bahrain", label: "Manama (GMT+3) — Bahrain" },
  { value: "Asia/Muscat", label: "Muscat (GMT+4) — Oman" },
  { value: "Asia/Tehran", label: "Tehran (GMT+3:30 / +4:30)" },
  { value: "Asia/Karachi", label: "Karachi (GMT+5)" },
  { value: "Asia/Kolkata", label: "Mumbai / Delhi (GMT+5:30)" },
  { value: "Asia/Singapore", label: "Singapore (GMT+8)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (GMT+8)" },
  { value: "Asia/Tokyo", label: "Tokyo (GMT+9)" },
  { value: "Europe/Paris", label: "Paris (GMT+1 / CEST GMT+2)" },
  { value: "Europe/Zurich", label: "Zurich (GMT+1 / CEST GMT+2)" },
  { value: "America/New_York", label: "New York (GMT-5 / EDT GMT-4)" },
  { value: "America/Los_Angeles", label: "Los Angeles (GMT-8 / PDT GMT-7)" },
  { value: "Australia/Sydney", label: "Sydney (GMT+10 / AEDT GMT+11)" },
];

function offsetForZone(zone: string, at: Date): string {
  // Use Intl to get the long offset (e.g. "GMT+04:00") for any IANA zone.
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longOffset",
    }).formatToParts(at);
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return tzPart.replace("GMT", "GMT").replace(/^GMT$/, "GMT+00:00");
  } catch {
    return "?";
  }
}

function formatInZone(at: Date, zone: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(at);
  } catch {
    return at.toISOString();
  }
}

function labelForZone(zone: string): string {
  const known = TIMEZONE_OPTIONS.find((o) => o.value === zone);
  return known ? known.label : zone;
}

function SystemClockTab() {
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const canEdit = hasPermission("manage_branding");

  // Live ticking clock (updates every second, purely client-side).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Server snapshot — refetch every 30s so we don't hammer the DB just to
  // verify a clock that drifts measurably only over hours.
  const clockQuery = useQuery<{
    serverNow: string;
    serverTimezone: string;
    dbNow: string | null;
    dbTimezone: string | null;
    configuredTimezone: string;
  }>({
    queryKey: ["/api/system-clock"],
    refetchInterval: 30_000,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const configured = clockQuery.data?.configuredTimezone || "Asia/Dubai";
  const effectiveSelection = selected ?? configured;

  // Fetch existing branding so the PUT below carries every required field.
  // We MUST NOT default these to empty strings — that would clobber the
  // company name, logo, base URL etc when the cache hasn't loaded yet.
  // Instead, the Save button stays disabled until this query has data.
  const brandingQuery = useQuery<{
    data: {
      companyName: string;
      departmentName: string;
      logoBase64: string | null;
      baseUrl: string;
      fileUploadsEnabled?: boolean;
      systemTimezone?: string;
    };
  }>({
    queryKey: ["/api/admin/branding"],
    enabled: canEdit,
  });
  const brandingReady = !!brandingQuery.data?.data;

  const saveMutation = useMutation({
    mutationFn: () => {
      const existing = brandingQuery.data?.data;
      if (!existing) {
        // Defensive: should never happen because the Save button is
        // disabled until brandingReady is true.
        throw new Error("Branding configuration not loaded yet — please wait a moment and try again.");
      }
      return apiRequest("PUT", "/api/admin/branding", {
        companyName: existing.companyName,
        departmentName: existing.departmentName,
        logoBase64: existing.logoBase64,
        baseUrl: existing.baseUrl,
        fileUploadsEnabled: existing.fileUploadsEnabled ?? true,
        systemTimezone: effectiveSelection,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/system-clock"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding"] });
      toast({ title: "Application timezone saved" });
      setSelected(null);
    },
    onError: (err: Error) => showFromApiError("Failed to save timezone", err),
  });

  if (clockQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const liveNow = new Date(); // tick triggers re-render

  // For "agreement" we compare the offset string (e.g. "GMT+04:00") of the
  // four zones against the configured zone — that's what the user really
  // cares about ("are all my clocks reading the same wall time?").
  const configuredOffset = offsetForZone(configured, liveNow);
  const rows = [
    {
      key: "server",
      label: "Server (Node process)",
      icon: Server,
      tz: clockQuery.data?.serverTimezone || "?",
      iso: clockQuery.data?.serverNow || liveNow.toISOString(),
      hint: "What the backend uses when it formats timestamps for emails, scheduler logs, and audit messages.",
    },
    {
      key: "db",
      label: "Database (Postgres)",
      icon: Hash,
      tz: clockQuery.data?.dbTimezone || "unavailable",
      iso: clockQuery.data?.dbNow || liveNow.toISOString(),
      hint: "Postgres always stores timestamps in UTC internally; this is just the session's display tz.",
    },
    {
      key: "browser",
      label: "Your browser",
      icon: Globe,
      tz: browserTimezone,
      iso: liveNow.toISOString(),
      hint: "What this browser uses when rendering dates in the UI. Changes per device, not server-controlled.",
    },
    {
      key: "configured",
      label: "Application configured",
      icon: Settings,
      tz: configured,
      iso: liveNow.toISOString(),
      hint: "The timezone this app should use as its 'company time'. Settable below.",
    },
  ];

  return (
    <div className="space-y-6">
      <Card className="bg-slate-900/50 border-cyan-500/20" data-testid="card-system-clock-overview">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Clock className="w-5 h-5 text-cyan-400" />
            System Clock — live diagnostic
          </CardTitle>
          <p className="text-sm text-white/60 mt-1">
            Compares the four clocks the application depends on. If any of them disagree on the wall time,
            something needs attention — most commonly the server's host timezone or the application's
            configured timezone setting below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/60 text-white/70">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Clock</th>
                  <th className="text-left px-3 py-2 font-semibold">Wall time (rendered in this zone)</th>
                  <th className="text-left px-3 py-2 font-semibold">Timezone</th>
                  <th className="text-left px-3 py-2 font-semibold">Offset</th>
                  <th className="text-left px-3 py-2 font-semibold">Match</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const Icon = r.icon;
                  const at = new Date(r.iso);
                  const wall = formatInZone(at, r.tz === "unavailable" ? "UTC" : r.tz);
                  const offset = r.tz === "unavailable" ? "?" : offsetForZone(r.tz, liveNow);
                  const matches = offset === configuredOffset && r.tz !== "unavailable";
                  return (
                    <tr key={r.key} className="border-t border-white/10" data-testid={`row-clock-${r.key}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 text-white">
                          <Icon className="w-4 h-4 text-cyan-400" />
                          <span className="font-medium">{r.label}</span>
                        </div>
                        <div className="text-xs text-white/50 mt-1 max-w-md">{r.hint}</div>
                      </td>
                      <td className="px-3 py-2 text-white/90 font-mono" data-testid={`text-clock-wall-${r.key}`}>{wall}</td>
                      <td className="px-3 py-2 text-white/80 font-mono">{r.tz}</td>
                      <td className="px-3 py-2 text-white/80 font-mono">{offset}</td>
                      <td className="px-3 py-2">
                        {r.tz === "unavailable" ? (
                          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-300 border-yellow-500/30">unavailable</Badge>
                        ) : matches ? (
                          <Badge variant="outline" className="bg-green-500/10 text-green-300 border-green-500/30">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> aligned
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-500/10 text-red-300 border-red-500/30">
                            <AlertTriangle className="w-3 h-3 mr-1" /> mismatch
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rounded-md bg-slate-800/40 border border-white/10 p-3 text-xs text-white/60 leading-relaxed">
            <strong className="text-white/80">How to read this:</strong> the "Application configured" row is the
            target. Every other row should show the same offset (e.g. <code className="text-cyan-300">GMT+04:00</code> for Abu Dhabi).
            A red <em>mismatch</em> on the <strong>Server</strong> row means the host OS is set to a different
            timezone — a system-level fix, not an application setting. A red mismatch on the <strong>Browser</strong> row
            just means this user is currently in a different country, which is normal and harmless. A red mismatch on
            the <strong>Database</strong> row means the Postgres session timezone differs — usually fine because
            timestamps are always stored in UTC, but worth noting.
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/50 border-cyan-500/20" data-testid="card-system-clock-setting">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Settings className="w-5 h-5 text-cyan-400" />
            Application timezone
          </CardTitle>
          <p className="text-sm text-white/60 mt-1">
            The timezone this application uses as its "company time" — applied wherever the system itself
            picks a display zone (email subject lines, scheduler messages, audit message text). Defaults to
            <strong className="text-white"> Abu Dhabi (GMT+4)</strong>. Each user's browser will continue to
            render dates in their own local zone in the UI; this setting is for everywhere the <em>server</em>
            picks the zone for them.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-white/80">Selected timezone</Label>
              <Select
                value={effectiveSelection}
                onValueChange={(v) => setSelected(v)}
                disabled={!canEdit || saveMutation.isPending}
              >
                <SelectTrigger className="mt-1 bg-slate-800 border-white/10 text-white" data-testid="select-system-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-white max-h-80">
                  {TIMEZONE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`option-tz-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-white/50 mt-1">
                Currently saved: <span className="text-white/80 font-mono">{labelForZone(configured)}</span>
                <span className="text-white/40"> (IANA: <code>{configured}</code>)</span>
              </div>
            </div>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={
                !canEdit ||
                !brandingReady ||
                saveMutation.isPending ||
                effectiveSelection === configured
              }
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="button-save-system-timezone"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
          {!canEdit && (
            <div className="text-xs text-yellow-300/80">
              You need the <code>manage_branding</code> permission to change this. Ask a super admin.
            </div>
          )}
          {canEdit && !brandingReady && (
            <div className="text-xs text-white/50 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading branding configuration before save is allowed…
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SystemLogTab({ onAcknowledge }: { onAcknowledge: () => void }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [categories, setCategories] = useState<string[]>([]);
  const [severities, setSeverities] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [grouped, setGrouped] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const limit = 50;

  const queryParams = new URLSearchParams();
  if (categories.length > 0) queryParams.set("categories", categories.join(","));
  if (severities.length > 0) queryParams.set("severities", severities.join(","));
  if (dateFrom) queryParams.set("dateFrom", dateFrom);
  if (dateTo) queryParams.set("dateTo", dateTo);
  if (search) queryParams.set("search", search);
  if (grouped) queryParams.set("grouped", "true");
  queryParams.set("page", String(page));
  queryParams.set("limit", String(limit));

  const logsQuery = useQuery<{ data: any[]; total: number; grouped?: boolean }>({
    queryKey: ["/api/system-logs", queryParams.toString()],
    queryFn: async () => {
      const res = await fetch(`/api/system-logs?${queryParams.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch system logs");
      return res.json();
    },
    refetchInterval: autoRefresh ? 15_000 : false,
  });

  const markReadMutation = useMutation({
    mutationFn: async (ids?: string[]) => {
      await apiRequest("POST", "/api/system-logs/mark-read", ids ? { ids } : {});
    },
    onSuccess: () => {
      toast({ title: "Marked as read" });
      queryClient.invalidateQueries({ queryKey: ["/api/system-logs"] });
      onAcknowledge();
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (confirmPassword: string) => {
      const res = await apiRequest("DELETE", "/api/system-logs", {
        confirmPassword,
        categories: categories.length > 0 ? categories : undefined,
        severities: severities.length > 0 ? severities : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: search || undefined,
      });
      // Defensive: a 200 OK can still carry a non-JSON body in edge cases
      // (proxy, SPA fallback during HMR, gateway page, etc). The actual
      // deletion is recorded in the audit log on the server regardless of
      // what the body looks like, so we never want a parse glitch to
      // surface as a scary "Unexpected token <" error to the user.
      const text = await res.text();
      let parsed: { deletedCount?: number } = {};
      try { parsed = text ? JSON.parse(text) : {}; } catch { /* tolerate non-JSON 200 */ }
      return { deletedCount: typeof parsed.deletedCount === "number" ? parsed.deletedCount : total };
    },
    onSuccess: (data) => {
      toast({ title: `Deleted ${data.deletedCount} log entr${data.deletedCount === 1 ? "y" : "ies"}` });
      setShowDeleteDialog(false);
      setDeletePassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/system-logs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/system-logs/unread-count"] });
      onAcknowledge();
    },
    onError: (err: Error) => showFromApiError("Delete failed", err),
  });

  const rows = logsQuery.data?.data || [];
  const total = logsQuery.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const toggleFilter = (list: string[], item: string, setter: (v: string[]) => void) => {
    setter(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
    setPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const downloadUrl = (format: string) => {
    const params = new URLSearchParams();
    if (categories.length > 0) params.set("categories", categories.join(","));
    if (severities.length > 0) params.set("severities", severities.join(","));
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (search) params.set("search", search);
    params.set("format", format);
    try { params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone); } catch {}
    return `/api/system-logs/download?${params.toString()}`;
  };

  return (
    <div className="space-y-4" data-testid="system-log-tab">
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-cyan-400" />
              System Diagnostics Log
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant={grouped ? "default" : "outline"}
                size="sm"
                className={`h-7 text-xs ${grouped ? "" : "border-white/20 text-white/60 hover:text-white"}`}
                onClick={() => { setGrouped((g) => !g); setPage(1); setExpandedRow(null); }}
                data-testid="button-toggle-grouped"
                title="Group identical errors so 200 copies become one row with a count and a first/last-seen timestamp."
              >
                <Layers className="w-3.5 h-3.5 mr-1" />
                {grouped ? "Grouped" : "Flat"}
              </Button>
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  data-testid="switch-auto-refresh"
                />
                <span className="text-xs text-white/50">Auto-refresh</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-white/60"
                onClick={() => logsQuery.refetch()}
                data-testid="button-refresh-logs"
              >
                <RefreshCw className={`w-4 h-4 ${logsQuery.isFetching ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1.5">
              <Label className="text-white/50 text-xs">Categories</Label>
              <div className="flex flex-wrap gap-1">
                {SYSTEM_LOG_CATEGORIES.map((cat) => (
                  <Button
                    key={cat.key}
                    variant={categories.includes(cat.key) ? "default" : "outline"}
                    size="sm"
                    className={`text-xs h-7 ${categories.includes(cat.key) ? "" : "border-white/20 text-white/60 hover:text-white"}`}
                    onClick={() => toggleFilter(categories, cat.key, setCategories)}
                    data-testid={`filter-category-${cat.key}`}
                  >
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/50 text-xs">Severity</Label>
              <div className="flex gap-1">
                {(["info", "warning", "error", "critical"] as const).map((sev) => {
                  const cfg = SEVERITY_CONFIG[sev];
                  const SevIcon = cfg.icon;
                  return (
                    <Button
                      key={sev}
                      variant={severities.includes(sev) ? "default" : "outline"}
                      size="sm"
                      className={`text-xs h-7 ${severities.includes(sev) ? "" : "border-white/20 text-white/60 hover:text-white"}`}
                      onClick={() => toggleFilter(severities, sev, setSeverities)}
                      data-testid={`filter-severity-${sev}`}
                    >
                      <SevIcon className={`w-3 h-3 mr-1 ${cfg.color}`} />
                      {sev.charAt(0).toUpperCase() + sev.slice(1)}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-white/50 text-xs">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="h-8 w-[150px] bg-white/5 border-white/20 text-white text-xs"
                data-testid="input-date-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/50 text-xs">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="h-8 w-[150px] bg-white/5 border-white/20 text-white text-xs"
                data-testid="input-date-to"
              />
            </div>
            <div className="flex gap-1 items-end">
              <Input
                placeholder="Search messages..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="h-8 w-[220px] bg-white/5 border-white/20 text-white text-xs"
                data-testid="input-search-logs"
              />
              <Button variant="ghost" size="sm" className="h-8 text-white/60" onClick={handleSearch} data-testid="button-search-logs">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-1 ml-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-white/20 text-white/60 hover:text-white"
                onClick={() => markReadMutation.mutate(undefined)}
                disabled={markReadMutation.isPending}
                data-testid="button-mark-all-read"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Mark All Read
              </Button>
              <a href={downloadUrl("csv")} download data-testid="button-download-csv">
                <Button variant="outline" size="sm" className="h-8 text-xs border-white/20 text-white/60 hover:text-white">
                  <FileDown className="w-3.5 h-3.5 mr-1" />
                  CSV
                </Button>
              </a>
              <a href={downloadUrl("xlsx")} download data-testid="button-download-xlsx">
                <Button variant="outline" size="sm" className="h-8 text-xs border-white/20 text-white/60 hover:text-white">
                  <FileDown className="w-3.5 h-3.5 mr-1" />
                  XLSX
                </Button>
              </a>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-red-400/30 text-red-300 hover:bg-red-400/10 hover:text-red-200"
                onClick={() => { setDeletePassword(""); setShowDeleteDialog(true); }}
                disabled={!dateTo}
                title={dateTo ? "Permanently delete log entries matching the active filters." : "Set a 'To' date first — the upper bound is required so a typo can't wipe everything."}
                data-testid="button-bulk-delete-logs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete…
              </Button>
            </div>
          </div>

          {showDeleteDialog && (
            <div
              className="rounded-md border border-red-400/30 bg-red-950/40 p-4 space-y-3"
              data-testid="panel-bulk-delete-confirm"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-100 font-medium">
                    Permanently delete {total.toLocaleString()} log entr{total === 1 ? "y" : "ies"}?
                  </p>
                  <p className="text-xs text-red-200/70 mt-1">
                    This deletes every entry currently matching your filters
                    {dateFrom ? <> from <span className="font-mono text-red-100">{dateFrom}</span></> : <> up</>}
                    {" to "}<span className="font-mono text-red-100">{dateTo}</span>
                    {categories.length > 0 && <> in categories: <span className="font-mono text-red-100">{categories.join(", ")}</span></>}
                    {severities.length > 0 && <> at severities: <span className="font-mono text-red-100">{severities.join(", ")}</span></>}
                    {search && <> matching “<span className="font-mono text-red-100">{search}</span>”</>}
                    . This cannot be undone — download a CSV/XLSX first if you need a copy. The deletion itself will be recorded as a new <span className="font-mono text-red-100">system / warning</span> entry showing who did it, when, and how many rows were removed.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Confirm with your password"
                  className="bg-white/5 border-white/10 text-white h-8 text-xs flex-1 max-w-xs"
                  data-testid="input-bulk-delete-password"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-red-400/40 text-red-200 hover:bg-red-400/20"
                  disabled={!deletePassword || bulkDeleteMutation.isPending || total === 0}
                  onClick={() => bulkDeleteMutation.mutate(deletePassword)}
                  data-testid="button-bulk-delete-confirm"
                >
                  {bulkDeleteMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  Delete {total.toLocaleString()} entr{total === 1 ? "y" : "ies"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-white/60"
                  onClick={() => { setShowDeleteDialog(false); setDeletePassword(""); }}
                  disabled={bulkDeleteMutation.isPending}
                  data-testid="button-bulk-delete-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {logsQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No log entries found</p>
              <p className="text-xs mt-1">Adjust your filters or check back later.</p>
            </div>
          ) : grouped ? (
            <div className="space-y-1" data-testid="grouped-rows">
              {rows.map((row: any) => {
                const sev = row.latest_severity as keyof typeof SEVERITY_CONFIG;
                const sevCfg = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.info;
                const SevIcon = sevCfg.icon;
                const catCfg = SYSTEM_LOG_CATEGORIES.find((c) => c.key === row.latest_category);
                const drillDown = () => {
                  // Switch to flat view filtered to this group so the user
                  // can see every individual occurrence. Uses the latest
                  // message (first 60 chars) as the search term — same key
                  // shape the backend uses for grouping when no app frame
                  // is present, so the filter is reliably narrow.
                  setGrouped(false);
                  setSearch(String(row.latest_message || "").slice(0, 60));
                  setSearchInput(String(row.latest_message || "").slice(0, 60));
                  setPage(1);
                };
                const firstSeen = new Date(row.first_seen);
                const lastSeen = new Date(row.last_seen);
                const sameInstant = firstSeen.getTime() === lastSeen.getTime();
                return (
                  <button
                    key={row.dedup_key}
                    onClick={drillDown}
                    className={`w-full text-left rounded-md border ${row.unack_count > 0 ? sevCfg.bg : "bg-white/3 border-white/8"} px-3 py-2 flex items-center gap-3 hover:bg-white/8 transition-colors`}
                    data-testid={`row-grouped-${row.dedup_key.replace(/[^a-z0-9]/gi, "_").slice(0, 60)}`}
                  >
                    <SevIcon className={`w-4 h-4 flex-shrink-0 ${sevCfg.color}`} />
                    <span className={`text-xs uppercase tracking-wider font-medium w-20 flex-shrink-0 ${catCfg?.color || "text-white/50"}`}>
                      {row.latest_category}
                    </span>
                    <span className="text-xs text-white/80 flex-1 truncate">{row.latest_message}</span>
                    <span
                      className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${row.count > 1 ? "bg-amber-400/20 text-amber-200" : "bg-white/5 text-white/50"}`}
                      data-testid={`badge-count-${row.dedup_key.replace(/[^a-z0-9]/gi, "_").slice(0, 60)}`}
                    >
                      ×{row.count}
                    </span>
                    {row.unack_count > 0 && row.unack_count !== row.count && (
                      <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-red-400/20 text-red-200 flex-shrink-0">
                        {row.unack_count} new
                      </span>
                    )}
                    <span className="text-xs text-white/30 flex-shrink-0 flex items-center gap-1 w-[170px] justify-end" title={`First: ${firstSeen.toLocaleString()}\nLast: ${lastSeen.toLocaleString()}`}>
                      <Clock className="w-3 h-3" />
                      {sameInstant ? lastSeen.toLocaleString() : `${firstSeen.toLocaleDateString()} → ${lastSeen.toLocaleString()}`}
                    </span>
                  </button>
                );
              })}
              <p className="text-sm text-white/30 pt-2">
                Click any row to drill in to the individual occurrences. Counts and timestamps respect the active filters above.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {rows.map((row: any) => {
                const sevCfg = SEVERITY_CONFIG[row.severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info;
                const SevIcon = sevCfg.icon;
                const catCfg = SYSTEM_LOG_CATEGORIES.find((c) => c.key === row.category);
                const isExpanded = expandedRow === row.id;
                return (
                  <div key={row.id} className={`rounded-md border ${row.acknowledged ? "bg-white/3 border-white/8" : sevCfg.bg} transition-colors`}>
                    <button
                      className="w-full text-left px-3 py-2 flex items-center gap-3"
                      onClick={() => setExpandedRow(isExpanded ? null : row.id)}
                      data-testid={`row-log-${row.id}`}
                    >
                      <SevIcon className={`w-4 h-4 flex-shrink-0 ${sevCfg.color}`} />
                      <span className={`text-xs uppercase tracking-wider font-medium w-20 flex-shrink-0 ${catCfg?.color || "text-white/50"}`}>
                        {row.category}
                      </span>
                      <span className="text-xs text-white/80 flex-1 truncate">{row.message}</span>
                      <span className="text-xs text-white/30 flex-shrink-0 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(row.timestamp).toLocaleString()}
                      </span>
                      {!row.acknowledged && (
                        <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-white/5 space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-white/40">Category:</span>{" "}
                            <span className={catCfg?.color || "text-white/70"}>{row.category}</span>
                          </div>
                          <div>
                            <span className="text-white/40">Severity:</span>{" "}
                            <span className={sevCfg.color}>{row.severity}</span>
                          </div>
                          {row.related_entity_type && (
                            <div>
                              <span className="text-white/40">Entity:</span>{" "}
                              <span className="text-white/70">{row.related_entity_type} {row.related_entity_id || ""}</span>
                            </div>
                          )}
                        </div>
                        {row.details && <LogDetailRenderer details={row.details} relatedEntityType={row.related_entity_type} relatedEntityId={row.related_entity_id} />}
                        <RelatedLogs entryId={row.id} onJump={(id) => setExpandedRow(id)} />
                        {row.acknowledged && row.acknowledged_at && (
                          <div className="text-sm text-white/40 flex items-center gap-1.5 pt-1 border-t border-white/5" data-testid={`text-acknowledged-${row.id}`}>
                            <CheckCircle className="w-3 h-3 text-green-400/60" />
                            <span>
                              Acknowledged by{" "}
                              <span className="text-white/70">{row.acknowledged_by_username || "unknown"}</span>
                              {" "}on {new Date(row.acknowledged_at).toLocaleString()}
                            </span>
                          </div>
                        )}
                        {!row.acknowledged && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-white/50 hover:text-white"
                            onClick={(e) => { e.stopPropagation(); markReadMutation.mutate([row.id]); }}
                            data-testid={`button-acknowledge-${row.id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Acknowledge
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-white/40">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-white/60"
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-white/50 flex items-center px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="text-white/60"
                  data-testid="button-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
